import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "motion/react";
import { translations } from "./translations";
import { comparativeMatrix } from "./starsData";
import ArchetypeSelector from "./components/ArchetypeSelector";
import DeepSpaceParticles from "./components/DeepSpaceParticles";
import PleiadesTracker from "./components/PleiadesTracker";
import CosmicQuickJump from "./components/CosmicQuickJump";
import CosmicSharing from "./components/CosmicSharing";
import { parseTextWithGlossary, registerGlossaryCallback, unregisterGlossaryCallback } from "./components/CosmicGlossary";
import StellarNavigation from "./components/StellarNavigation";
import { getFormattedLastUpdate } from "./utils/buildTimestamp";
import { updateDocumentSeo, SUBPAGE_SEO_CONFIGS, getSubpagePath, getSubpageIdFromPath } from "./utils/seo";

// Code splitting / lazy loading major heavy components and modals
const StarMap = lazy(() => import("./components/StarMap"));
const FrequencySynth = lazy(() => import("./components/FrequencySynth"));
const SubpageModal = lazy(() => import("./components/SubpageModal"));
const SpecialArchiveModal = lazy(() => import("./components/SpecialArchiveModal").then(module => ({ default: module.SpecialArchiveModal })));
const TermsModal = lazy(() => import("./components/TermsModal"));
const MemberAccessModal = lazy(() => import("./components/MemberAccessModal"));
const SitemapModal = lazy(() => import("./components/SitemapModal"));
const CosmicGlossary = lazy(() => import("./components/CosmicGlossary").then(module => ({ default: module.CosmicGlossary })));
const MemberSection = lazy(() => import("./components/MemberSection"));
const WallpapersSubpage = lazy(() => import("./components/WallpapersSubpage"));
const BattleDossiersSubpage = lazy(() => import("./components/BattleDossiersSubpage"));
const ChroniclesOfLyraSubpage = lazy(() => import("./components/ChroniclesOfLyraSubpage"));
const WhatAreLyraChroniclesSubpage = lazy(() => import("./components/WhatAreLyraChroniclesSubpage"));
const LyraChroniclesLastRecordSubpage = lazy(() => import("./components/LyraChroniclesLastRecordSubpage"));
const PleiadesLibrarySubpage = lazy(() => import("./components/PleiadesLibrarySubpage"));
const LyraChroniclesArchiveSubpage = lazy(() => import("./components/LyraChroniclesArchiveSubpage"));
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { doc, onSnapshot, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./lib/firebase";
import {
  Sparkles,
  Compass,
  Zap,
  BookOpen,
  Info,
  Layers,
  Globe,
  Star,
  Activity,
  ArrowUpRight,
  BookMarked,
  Twitter,
  Facebook,
  Linkedin,
  Share2,
  Send,
  MessageSquare,
  Mail,
  Link2,
  Check,
  Palette,
  Menu,
  X,
  Database,
  Radio,
  Flame,
  Lock,
  RefreshCw,
  Newspaper,
  ExternalLink,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Clock,
  UserCheck,
  ChevronRight
} from "lucide-react";

const heroBannerImg = new URL("./assets/images/pleiades_hero_banner_1783146202370.jpg", import.meta.url).href;
const pleiadWomanImg = new URL("./assets/images/A Pleiad woman stands on the terrace.webp", import.meta.url).href;
const pleiadianTechImg = new URL("./assets/images/Pleiadian Technology.webp", import.meta.url).href;
const pleiadesLogoImg = new URL("./assets/images/pleiades-m45-logo.jpg", import.meta.url).href;
const pleiadiansOnTerraceImg = new URL("./assets/images/Pleiadians on a terrace.webp", import.meta.url).href;
const pleiadianSilentArkImg = new URL("./assets/images/wallpapers/Pleiadian prophecy of the Silent Ark's return.webp", import.meta.url).href;
const pleiadianM45ClusterImg = new URL("./assets/images/Pleiadian M45 cluster.jpg", import.meta.url).href;
const mythologyImg = new URL("./assets/images/Mythology.webp", import.meta.url).href;
const erranStarfieldMapImg = new URL("./assets/images/ERRAN STARFIELD MAP Holographic Navigation Grid — Taygeta Sector.webp", import.meta.url).href;
const lyranOrionWarImg = new URL("./assets/images/lyran_orion_war.webp", import.meta.url).href;

interface ThemePreset {
  id: string;
  nameEn: string;
  nameHr: string;
  colors: {
    "--cosmic-black": string;
    "--cosmic-indigo": string;
    "--pleiadian-blue": string;
    "--celestial-light": string;
    "--ethereal-white": string;
    "--quantum-turquoise": string;
    "--mystic-purple": string;
    "--golden-resonance": string;
  };
}

const COSMIC_THEMES: ThemePreset[] = [
  {
    id: "nebula-deep-blue",
    nameEn: "Nebula Deep Blue",
    nameHr: "Duboko Plava Nebula",
    colors: {
      "--cosmic-black": "#0A0F1C",
      "--cosmic-indigo": "#1C2A4D",
      "--pleiadian-blue": "#4A90E2",
      "--celestial-light": "#7FB8FF",
      "--ethereal-white": "#C9E6FF",
      "--quantum-turquoise": "#00D4FF",
      "--mystic-purple": "#A67CFF",
      "--golden-resonance": "#FFD27F",
    }
  },
  {
    id: "crystalline-gold",
    nameEn: "Crystalline Gold",
    nameHr: "Kristalno Zlato",
    colors: {
      "--cosmic-black": "#0F0C05",
      "--cosmic-indigo": "#2E220B",
      "--pleiadian-blue": "#E6A119",
      "--celestial-light": "#FCD34D",
      "--ethereal-white": "#FFF3D1",
      "--quantum-turquoise": "#F59E0B",
      "--mystic-purple": "#D97706",
      "--golden-resonance": "#FBBF24",
    }
  },
  {
    id: "high-voltage-violet",
    nameEn: "High-Voltage Violet",
    nameHr: "Visokonaponska Ljubičasta",
    colors: {
      "--cosmic-black": "#080412",
      "--cosmic-indigo": "#1D0B33",
      "--pleiadian-blue": "#A855F7",
      "--celestial-light": "#C084FC",
      "--ethereal-white": "#F3E8FF",
      "--quantum-turquoise": "#EC4899",
      "--mystic-purple": "#A855F7",
      "--golden-resonance": "#F472B6",
    }
  }
];

const isHrRoute = (path: string): boolean => {
  const p = path.toLowerCase().replace(/\/$/, "");
  const hrSlugs = [
    "/kronike-lyre",
    "/sto-su-kronike-lyre",
    "/kronike-lyre/posljednji-zapis",
    "/posljednji-zapis",
    "/prolog-posljednji-zapis",
    "/kronike-lyre/arhiv",
    "/veliki-arhiv",
    "/plejade-mitologija",
    "/plejadanska-civilizacija",
    "/plejadanska-tehnologija",
    "/plejadani-ljudska-povijest",
    "/komparativna-kozmologija",
    "/evolucija-svijesti",
    "/plejadanski-identitet",
    "/lirsko-orionski-rat",
    "/velika-evakuacija",
    "/pad-lyre",
    "/knjige-fragmenata",
    "/knjiznica"
  ];
  return hrSlugs.some(slug => p === slug || p.startsWith(slug + "/"));
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [language, setLanguage] = useState<"en" | "hr">(() => {
    if (typeof window !== "undefined") {
      const p = window.location.pathname.toLowerCase().replace(/\/$/, "");
      if (isHrRoute(p)) return "hr";
    }
    return "en";
  });
  const [activeSynthFreq, setActiveSynthFreq] = useState<number | undefined>(undefined);
  const [activeSubpageId, setActiveSubpageId] = useState<number | null>(null);
  const [isSubpageOpen, setIsSubpageOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  const [activeGlossaryTermId, setActiveGlossaryTermId] = useState<string | null>(null);
  const [isTelemetryOpen, setIsTelemetryOpen] = useState(false);
  const [isSynthOpen, setIsSynthOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isMemberAccessModalOpen, setIsMemberAccessModalOpen] = useState(false);
  const [isErranHoloOpen, setIsErranHoloOpen] = useState(false);
  const [isSitemapOpen, setIsSitemapOpen] = useState(false);
  const [activeSpecialArchiveId, setActiveSpecialArchiveId] = useState<"seventh-ship" | "first-separation" | "signal-lost" | null>(null);
  const [isSpecialArchiveOpen, setIsSpecialArchiveOpen] = useState(false);
  const [isWallpapersOpen, setIsWallpapersOpen] = useState(false);
  const [isDossiersOpen, setIsDossiersOpen] = useState(false);
  const [isChroniclesOpen, setIsChroniclesOpen] = useState(false);
  const [isLyraPrologueOpen, setIsLyraPrologueOpen] = useState(false);
  const [isLyraLastRecordOpen, setIsLyraLastRecordOpen] = useState(false);
  const [isLibrarySubpageOpen, setIsLibrarySubpageOpen] = useState(false);
  const [isLyraArchiveOpen, setIsLyraArchiveOpen] = useState(false);

  // Sync document language with state to inform browsers
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Clean Route Mapping dictionaries for SEO
  const SECTION_TO_PATH_MAP: Record<string, string> = {
    "intro-section": "/intro",
    "history-section": "/history",
    "clans-section": "/clans",
    "tech-desc-section": "/technology",
    "humanity-section": "/contact",
    "comparison-section": "/cosmology",
    "evolution-section": "/evolution",
    "interactive-portal": "/star-map",
    "star-map-section": "/star-map",
    "library-section": "/library",
    "member-gate-section": "/portal",
    "synth-section": "/synth",
    "hero-section": "/",
  };

  const PATH_TO_SECTION_MAP: Record<string, string> = {
    "/": "hero-section",
    "/intro": "intro-section",
    "/history": "history-section",
    "/clans": "clans-section",
    "/technology": "tech-desc-section",
    "/contact": "humanity-section",
    "/cosmology": "comparison-section",
    "/comparison": "comparison-section",
    "/lyra-orion": "comparison-section",
    "/evolution": "evolution-section",
    "/star-map": "interactive-portal",
    "/map": "interactive-portal",
    "/library": "library-section",
    "/portal": "member-gate-section",
    "/member": "member-gate-section",
    "/synth": "synth-section",
  };

  // States and helpers for sharing specific archives
  const [copiedArchiveId, setCopiedArchiveId] = useState<string | null>(null);

  const getShareUrlForArchive = (archiveId: string) => {
    if (typeof window !== "undefined") {
      return `${window.location.origin}/archive/${archiveId}`;
    }
    return `https://pleiades-m45.com/archive/${archiveId}`;
  };

  const handleCopyArchiveLink = async (archiveId: string) => {
    try {
      await navigator.clipboard.writeText(getShareUrlForArchive(archiveId));
      setCopiedArchiveId(archiveId);
      setTimeout(() => setCopiedArchiveId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  useEffect(() => {
    const pathname = location.pathname.toLowerCase().replace(/\/$/, "") || "/";
    const params = new URLSearchParams(location.search);

    let targetPath = pathname;

    // Backward compatibility for legacy query parameters
    if (params.get("archive")) {
      targetPath = `/archive/${params.get("archive")}`;
    } else if (params.get("library")) {
      targetPath = `/library/${params.get("library")}`;
    } else if (params.get("section")) {
      const sec = params.get("section")!;
      targetPath = SECTION_TO_PATH_MAP[sec] || `/${sec.replace("-section", "")}`;
    } else if (params.get("glossary")) {
      const term = params.get("glossary");
      targetPath = term && term !== "true" ? `/glossary/${term}` : "/glossary";
    } else if (params.get("page") === "wallpapers" || params.get("wallpapers") === "true") {
      targetPath = "/wallpapers";
    } else if (params.get("page") === "dossiers" || params.get("dossiers") === "true") {
      targetPath = "/dossiers";
    }

    // Automatically strip legacy query strings into clean URLs in address bar via React Router
    if (location.search || targetPath !== pathname) {
      navigate(targetPath, { replace: true });
    }

    // Process target route
    if (targetPath === "/") {
      setIsLibrarySubpageOpen(false);
      setIsSubpageOpen(false);
      setIsSpecialArchiveOpen(false);
      setIsWallpapersOpen(false);
      setIsDossiersOpen(false);
      setIsChroniclesOpen(false);
      setIsLyraPrologueOpen(false);
      setIsLyraArchiveOpen(false);
      setIsGlossaryOpen(false);
      setIsTermsOpen(false);
      setIsMemberAccessModalOpen(false);
      setIsSitemapOpen(false);
    } else if (targetPath.startsWith("/archive/")) {
      const archive = targetPath.replace("/archive/", "");
      if (archive === "seventh-ship" || archive === "first-separation" || archive === "signal-lost") {
        setActiveSpecialArchiveId(archive as any);
        setIsSpecialArchiveOpen(true);
        setTimeout(() => {
          const el = document.getElementById("library-section");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    } else if (getSubpageIdFromPath(targetPath) !== null) {
      const subId = getSubpageIdFromPath(targetPath)!;
      setIsLibrarySubpageOpen(false);
      setActiveSubpageId(subId);
      setIsSubpageOpen(true);
      const isPathHr = isHrRoute(targetPath);
      if (isPathHr && language !== "hr") {
        setLanguage("hr");
      }
      const canonicalSlug = getSubpagePath(subId, isPathHr ? false : (language === "en"));
      if (pathname.startsWith("/library/") || (pathname !== canonicalSlug && !targetPath.includes(canonicalSlug))) {
        navigate(canonicalSlug, { replace: true });
      }
      setTimeout(() => {
        const el = document.getElementById("library-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    } else if (targetPath === "/wallpapers") {
      setIsWallpapersOpen(true);
    } else if (targetPath === "/dossiers" || targetPath === "/battle-dossiers") {
      setIsDossiersOpen(true);
    } else if (targetPath === "/chronicles" || targetPath === "/chronicles-of-lyra" || targetPath === "/kronike-lyre" || targetPath === "/lyra-chronicles" || targetPath === "/kronike") {
      if (targetPath === "/kronike-lyre" || targetPath === "/kronike") {
        if (language !== "hr") setLanguage("hr");
      } else if (targetPath === "/lyra-chronicles" || targetPath === "/chronicles-of-lyra") {
        if (language !== "en") setLanguage("en");
      }
      setIsChroniclesOpen(true);
    } else if (targetPath === "/sto-su-kronike-lyre" || targetPath === "/what-are-lyra-chronicles" || targetPath === "/lyra-prologue" || targetPath === "/prolog-lyre" || targetPath === "/prolog") {
      if (targetPath === "/sto-su-kronike-lyre" || targetPath === "/prolog-lyre") {
        if (language !== "hr") setLanguage("hr");
      } else if (targetPath === "/what-are-lyra-chronicles") {
        if (language !== "en") setLanguage("en");
      }
      setIsLyraPrologueOpen(true);
    } else if (targetPath === "/kronike-lyre/posljednji-zapis" || targetPath === "/posljednji-zapis" || targetPath === "/last-record" || targetPath === "/lyra-last-record" || targetPath === "/lyra-chronicles/last-record" || targetPath === "/prolog-posljednji-zapis" || targetPath === "/prolog-2") {
      if (targetPath.includes("kronike-lyre") || targetPath === "/posljednji-zapis") {
        if (language !== "hr") setLanguage("hr");
      } else if (targetPath.includes("lyra-chronicles") || targetPath === "/last-record") {
        if (language !== "en") setLanguage("en");
      }
      setIsLyraLastRecordOpen(true);
    } else if (targetPath === "/kronike-lyre/arhiv" || targetPath === "/lyra-archive" || targetPath === "/lyra-chronicles/archive" || targetPath === "/veliki-arhiv" || targetPath === "/digitalni-arhiv" || targetPath === "/arhiv") {
      if (targetPath.includes("kronike-lyre") || targetPath === "/veliki-arhiv" || targetPath === "/arhiv") {
        if (language !== "hr") setLanguage("hr");
      } else if (targetPath.includes("lyra-chronicles") || targetPath === "/lyra-archive") {
        if (language !== "en") setLanguage("en");
      }
      setIsLyraArchiveOpen(true);
    } else if (targetPath === "/pleiades-library" || targetPath === "/library-archive" || targetPath === "/knjiznica") {
      if (targetPath === "/knjiznica" && language !== "hr") setLanguage("hr");
      setIsSubpageOpen(false);
      setIsLibrarySubpageOpen(true);
    } else if (targetPath.startsWith("/glossary")) {
      const term = targetPath.replace("/glossary/", "").replace("/glossary", "");
      setActiveGlossaryTermId(term || null);
      setIsGlossaryOpen(true);
    } else if (targetPath === "/terms") {
      setIsTermsOpen(true);
    } else if (targetPath === "/member-access") {
      setIsMemberAccessModalOpen(true);
    } else if (targetPath === "/sitemap") {
      setIsSitemapOpen(true);
    } else if (PATH_TO_SECTION_MAP[targetPath]) {
      setIsLibrarySubpageOpen(false);
      setIsSubpageOpen(false);
      setIsSpecialArchiveOpen(false);
      setIsWallpapersOpen(false);
      setIsDossiersOpen(false);
      setIsChroniclesOpen(false);
      setIsGlossaryOpen(false);
      setIsTermsOpen(false);
      setIsMemberAccessModalOpen(false);
      setIsSitemapOpen(false);
      const sectionId = PATH_TO_SECTION_MAP[targetPath];
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const headerOffset = window.innerWidth < 768 ? 75 : 115;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + (window.scrollY || window.pageYOffset) - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        } else if (sectionId === "hero-section") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 300);
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    registerGlossaryCallback((termId) => {
      handleOpenGlossary(termId);
    });
    return () => {
      unregisterGlossaryCallback();
    };
  }, []);

  // Dynamic Search Engine Optimization (SEO) & Social Cards Synchronization
  useEffect(() => {
    const isEn = language === "en";
    if (isLibrarySubpageOpen) {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.pleiadesLibrary, isEn);
    } else if (isSubpageOpen && activeSubpageId !== null) {
      if (activeSubpageId === 11) {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.omegaBlack, isEn);
      } else if (activeSubpageId === 12) {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.genesisRoster, isEn);
      } else if (activeSubpageId === 13) {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.booksOfFragments, isEn);
      } else {
        const subConfig = SUBPAGE_SEO_CONFIGS[`subpage${activeSubpageId}`];
        if (subConfig) {
          updateDocumentSeo(subConfig, isEn);
        } else {
          updateDocumentSeo(SUBPAGE_SEO_CONFIGS.home, isEn);
        }
      }
    } else if (isLyraPrologueOpen) {
      updateDocumentSeo((SUBPAGE_SEO_CONFIGS as any).lyraPrologue || SUBPAGE_SEO_CONFIGS.chronicles, isEn);
    } else if (isLyraLastRecordOpen) {
      updateDocumentSeo((SUBPAGE_SEO_CONFIGS as any).lyraLastRecord || SUBPAGE_SEO_CONFIGS.chronicles, isEn);
    } else if (isLyraArchiveOpen) {
      updateDocumentSeo((SUBPAGE_SEO_CONFIGS as any).lyraArchive || SUBPAGE_SEO_CONFIGS.chronicles, isEn);
    } else if (isChroniclesOpen) {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.chronicles, isEn);
    } else if (isDossiersOpen) {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.dossiers, isEn);
    } else if (isWallpapersOpen) {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.wallpapers, isEn);
    } else if (isSpecialArchiveOpen && activeSpecialArchiveId) {
      if (activeSpecialArchiveId === "seventh-ship") {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.seventhShip, isEn);
      } else if (activeSpecialArchiveId === "first-separation") {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.firstSeparation, isEn);
      } else if (activeSpecialArchiveId === "signal-lost") {
        updateDocumentSeo(SUBPAGE_SEO_CONFIGS.signalLost, isEn);
      }
    } else if (isGlossaryOpen) {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.glossary, isEn);
    } else {
      updateDocumentSeo(SUBPAGE_SEO_CONFIGS.home, isEn);
    }
  }, [
    language,
    isLibrarySubpageOpen,
    isSubpageOpen,
    activeSubpageId,
    isChroniclesOpen,
    isLyraPrologueOpen,
    isLyraLastRecordOpen,
    isLyraArchiveOpen,
    isSpecialArchiveOpen,
    activeSpecialArchiveId,
    isDossiersOpen,
    isWallpapersOpen,
    isGlossaryOpen
  ]);

  // Hero section image rotation
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const heroImages = [pleiadiansOnTerraceImg, pleiadianSilentArkImg, pleiadianM45ClusterImg];

  useEffect(() => {
    // Preload hero rotation images
    heroImages.forEach((imgSrc) => {
      const img = new Image();
      img.src = imgSrc;
    });

    const timer = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  // Erran Starfield Map zoom and pan state
  const [mapScale, setMapScale] = useState(1);
  const [mapPosition, setMapPosition] = useState({ x: 0, y: 0 });
  const [isDraggingMap, setIsDraggingMap] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);

  // Scanning progress & glitch state
  const [scanProgress, setScanProgress] = useState(0);
  const [isGlitching, setIsGlitching] = useState(false);
  const [hoverCoords, setHoverCoords] = useState<{
    clientX: number;
    clientY: number;
    containerX: number;
    containerY: number;
    rectWidth: number;
    rectHeight: number;
    ra: string;
    dec: string;
    sector: string;
    resonance: string;
  } | null>(null);

  // Live decrypt progress state & email subscription
  const [decryptPct, setDecryptPct] = useState(85.41235);
  const [downlinkEmail, setDownlinkEmail] = useState("");
  const [botField, setBotField] = useState("");
  const [downlinkStatus, setDownlinkStatus] = useState<"idle" | "submitting" | "success">("idle");
  const [downlinkError, setDownlinkError] = useState<string | null>(null);
  const [omegaProtocolStatus, setOmegaProtocolStatus] = useState<"idle" | "initiated">("idle");
  const [verifiedNodesCount, setVerifiedNodesCount] = useState<number>(() => {
    const saved = localStorage.getItem("omega_verified_nodes");
    return saved ? parseInt(saved, 10) : 0;
  });

  useEffect(() => {
    const decryptInterval = setInterval(() => {
      setDecryptPct(prev => {
        const increment = Math.random() * 0.00018;
        const next = prev + increment;
        return next >= 100 ? 85.41235 : next;
      });
    }, 1500);

    return () => clearInterval(decryptInterval);
  }, []);

  useEffect(() => {
    // Synchronize progress bar with laser beam's 6-second one-way sweep (12 seconds round-trip)
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - startTime) % 6000;
      const pct = Math.floor((elapsed / 6000) * 100);
      setScanProgress(pct);
    }, 100);

    // Glitch effect triggers every 10 seconds and lasts for 400ms
    const glitchInterval = setInterval(() => {
      setIsGlitching(true);
      setTimeout(() => {
        setIsGlitching(false);
      }, 400);
    }, 10000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(glitchInterval);
    };
  }, []);

  const clampPosition = (x: number, y: number, scale: number) => {
    if (scale <= 1) return { x: 0, y: 0 };
    // Boundaries are proportional to scale to keep the image centered and prevent panning too far out of view
    const maxShiftX = Math.max(0, 400 * (scale - 1));
    const maxShiftY = Math.max(0, 250 * (scale - 1));
    return {
      x: Math.max(-maxShiftX, Math.min(maxShiftX, x)),
      y: Math.max(-maxShiftY, Math.min(maxShiftY, y)),
    };
  };

  const handleZoomIn = () => {
    setMapScale((prev) => {
      const next = Math.min(4, prev + 0.5);
      setMapPosition((pos) => clampPosition(pos.x, pos.y, next));
      return next;
    });
  };

  const handleZoomOut = () => {
    setMapScale((prev) => {
      const next = Math.max(1, prev - 0.5);
      setMapPosition((pos) => clampPosition(pos.x, pos.y, next));
      return next;
    });
  };

  const handleResetZoom = () => {
    setMapScale(1);
    setMapPosition({ x: 0, y: 0 });
  };

  const handleOpenGlossary = (termId: string | null = null) => {
    const path = termId ? `/glossary/${termId}` : "/glossary";
    if (location.pathname !== path) {
      navigate(path);
    }
    setActiveGlossaryTermId(termId);
    setIsGlossaryOpen(true);
  };

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, (latest) => latest * 0.28);

  const [activeThemeId, setActiveThemeId] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("pleiades-cosmic-theme");
      if (saved && COSMIC_THEMES.some(t => t.id === saved)) {
        return saved;
      }
    }
    return "nebula-deep-blue";
  });

  useEffect(() => {
    const selectedTheme = COSMIC_THEMES.find(t => t.id === activeThemeId) || COSMIC_THEMES[0];
    Object.entries(selectedTheme.colors).forEach(([variableName, hexValue]) => {
      document.documentElement.style.setProperty(variableName, hexValue);
    });
    localStorage.setItem("pleiades-cosmic-theme", activeThemeId);
  }, [activeThemeId]);

  // Real-time Firestore user membership listener
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<string | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      console.log("[App.tsx] Auth state changed. User:", currentUser?.uid, currentUser?.email);
      setAuthUser(currentUser);

      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = null;
      }

      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        unsubscribeDoc = onSnapshot(
          userDocRef,
          (docSnap) => {
            console.log("[App.tsx] User Firestore snapshot - exists:", docSnap.exists(), "data:", docSnap.data());
            if (docSnap.exists()) {
              const data = docSnap.data();

              // Fallback fix: If user document is missing email (or is "No email") but Firebase Auth user has one, update Firestore
              if (currentUser.email && (!data?.email || data.email === "No email" || !data.email.trim())) {
                const healedEmail = currentUser.email.toLowerCase().trim();
                console.log("[App.tsx] Healing missing email for UID:", currentUser.uid, "to:", healedEmail);
                updateDoc(userDocRef, { email: healedEmail }).catch(() => {});
              }

              // Single session enforcement: ONLY applies to approved active members (and not taygeta@pleiades-m45.com)
              // Pending/unpaid users do NOT enforce or track activeSessionId
              if (data?.status === "active" && currentUser.email !== "taygeta@pleiades-m45.com") {
                const localSessionId = typeof window !== "undefined" ? sessionStorage.getItem("m45_device_session_id") : null;
                if (data?.activeSessionId && localSessionId && data.activeSessionId !== localSessionId) {
                  console.warn("[App.tsx] Single session enforcement: Account logged in from another device.");
                  signOut(auth);
                  setAuthUser(null);
                  setMembershipStatus(null);
                  setMembershipLoading(false);
                  return;
                }
              }
              setMembershipStatus(data?.status || "pending");
            } else {
              console.warn("[App.tsx] User document does not exist for UID:", currentUser.uid);
              // Auto-create document with email if Auth user exists
              if (currentUser.email) {
                setDoc(userDocRef, {
                  email: currentUser.email.toLowerCase().trim(),
                  status: "pending",
                  payment: "unpaid",
                  registeredAt: serverTimestamp(),
                  note: ""
                }).catch(() => {});
              }
              setMembershipStatus("pending");
            }
            setMembershipLoading(false);
          },
          (err) => {
            console.error("[App.tsx] Error fetching user status snapshot:", err);
            // Do NOT sign out user! Just preserve authUser and finish loading
            setMembershipLoading(false);
          }
        );
      } else {
        setMembershipStatus(null);
        setMembershipLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeDoc) unsubscribeDoc();
    };
  }, []);

  // Quick navigation scroll function
  const scrollToSection = (id: string, closeMobileMenu = true) => {
    const isMobileMenuCurrentlyOpen = isMobileMenuOpen;
    const cleanPath = SECTION_TO_PATH_MAP[id] || "/";
    if (location.pathname !== cleanPath) {
      navigate(cleanPath);
    }
    
    if (closeMobileMenu && isMobileMenuCurrentlyOpen) {
      // Close the menu immediately to trigger the exit transition
      setIsMobileMenuOpen(false);
      
      // Delay the measurement and scroll until the menu collapse has completed (~320ms)
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const headerOffset = window.innerWidth < 768 ? 75 : 115;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + (window.scrollY || window.pageYOffset) - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        } else if (id === "hero-section") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 320);
    } else {
      // Immediate scroll for desktop or when mobile menu is already closed
      const el = document.getElementById(id);
      if (el) {
        const headerOffset = window.innerWidth < 768 ? 75 : 115;
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + (window.scrollY || window.pageYOffset) - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      } else if (id === "hero-section") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const t = translations[language];

  // Callback when a user tunes into a star frequency in StarMap
  const handleTuneIn = (frequency: number) => {
    setActiveSynthFreq(frequency);
    setIsSynthOpen(true);
    scrollToSection("synth-section");
  };

  const handleOpenLibrary = () => {
    if (location.pathname !== "/pleiades-library") {
      navigate("/pleiades-library");
    }
    setIsLibrarySubpageOpen(true);
    setIsSubpageOpen(false);
  };

  const handleOpenSubpage = (id: number) => {
    const path = getSubpagePath(id, language === "en");
    if (location.pathname !== path) {
      navigate(path);
    }
    setIsLibrarySubpageOpen(false);
    setActiveSubpageId(id);
    setIsSubpageOpen(true);
  };

  return (
    <div className="min-h-screen bg-cosmic-black text-[#E8EEF7] font-sans selection:bg-quantum-turquoise/30 selection:text-white relative overflow-hidden">
      
      {/* Background Starfield & Immersive Nebula Design */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <DeepSpaceParticles activeFrequency={activeSynthFreq} themeId={activeThemeId} />
        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-cosmic-indigo rounded-full blur-[120px] opacity-40"></div>
        <div className="absolute bottom-[-5%] left-[-5%] w-[400px] h-[400px] bg-pleiadian-blue rounded-full blur-[100px] opacity-20"></div>
      </div>

      <CosmicQuickJump language={language} />
      <CosmicSharing language={language} />

      {/* Floating Header Wrapper */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <header className="bg-cosmic-black/90 md:bg-transparent md:bg-gradient-to-b md:from-cosmic-black md:via-cosmic-black/85 md:to-cosmic-black/45 backdrop-blur-lg border-b border-pleiadian-blue/15 px-4 md:px-12 flex flex-col gap-2.5 py-3 transition-all duration-300">
          
          {/* Row 1: Logo & Title (Left) + 9 Navigation Links (Right - Desktop) */}
          <div className="flex items-center justify-between w-full">
            <div 
              onClick={() => scrollToSection("hero-section")} 
              className="flex items-center gap-2.5 md:gap-3 cursor-pointer group select-none"
            >
              <div className="w-8 h-8 md:w-9 md:h-9 relative rounded-lg overflow-hidden border border-quantum-turquoise/30 shadow-[0_0_12px_rgba(0,212,255,0.4)] group-hover:border-quantum-turquoise group-hover:shadow-[0_0_18px_rgba(0,212,255,0.6)] bg-cosmic-black shrink-0 transition-all duration-300">
                <img
                  src={pleiadesLogoImg}
                  alt="Pleiades M45 Logo"
                  width="64"
                  height="64"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div>
                <div className="text-xs md:text-sm font-display font-bold tracking-[0.2em] uppercase text-white group-hover:text-quantum-turquoise flex items-center gap-1.5 leading-none transition-colors duration-300">
                  {t.projectName}
                  <span className="text-[10px] md:text-xs text-quantum-turquoise font-mono lowercase font-normal hidden sm:inline-block">
                    m45
                  </span>
                </div>
                <p className="text-[8px] md:text-[9px] font-mono tracking-widest text-celestial-light uppercase mt-0.5 leading-none hidden sm:block">
                  {t.projectSubtitle}
                </p>
              </div>
            </div>

            {/* Navigation Links - 9 Sections in One Single Row on Desktop */}
            <nav className="hidden md:flex flex-wrap items-center gap-x-3 lg:gap-x-4 xl:gap-x-5 text-[10px] lg:text-[11px] font-mono text-gray-300">
              <a 
                href="/intro"
                onClick={(e) => { e.preventDefault(); scrollToSection("intro-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "INTRO" : "UVOD"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/history"
                onClick={(e) => { e.preventDefault(); scrollToSection("history-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "HISTORY" : "POVIJEST"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/clans"
                onClick={(e) => { e.preventDefault(); scrollToSection("clans-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "CLANS" : "KLANOVI"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/technology"
                onClick={(e) => { e.preventDefault(); scrollToSection("tech-desc-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "TECH" : "TEHNOLOGIJA"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/contact"
                onClick={(e) => { e.preventDefault(); scrollToSection("humanity-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "CONTACT" : "KONTAKT"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/cosmology"
                onClick={(e) => { e.preventDefault(); scrollToSection("comparison-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "LYRA–ORION" : "LYRA–ORION"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/evolution"
                onClick={(e) => { e.preventDefault(); scrollToSection("evolution-section"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "EVOLUTION" : "EVOLUCIJA"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/star-map"
                onClick={(e) => { e.preventDefault(); scrollToSection("interactive-portal"); }} 
                className="uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-medium"
              >
                {language === "en" ? "MAP" : "KARTA"}
              </a>
              <span className="text-quantum-turquoise/25 select-none text-[8px]">•</span>
              <a 
                href="/library"
                onClick={(e) => { e.preventDefault(); scrollToSection("library-section"); }} 
                className="uppercase tracking-wider text-mystic-purple hover:text-quantum-turquoise transition-colors cursor-pointer font-bold px-1.5 py-0.5 bg-mystic-purple/10 border border-mystic-purple/30 rounded"
              >
                {language === "en" ? "LIBRARY" : "KNJIŽNICA"}
              </a>
            </nav>

            {/* Mobile Header Right Actions */}
            <div className="flex md:hidden items-center gap-2">
              {/* Mobile Membership Status Pill */}
              <button
                onClick={() => scrollToSection("member-gate-section")}
                className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[9px] font-mono uppercase tracking-wider transition-all cursor-pointer shrink-0 ${
                  membershipLoading
                    ? "bg-cosmic-black/60 border-gray-700 text-gray-400"
                    : authUser
                    ? membershipStatus === "active"
                      ? "bg-emerald-950/90 border-emerald-500/60 text-emerald-300"
                      : "bg-amber-950/90 border-amber-500/60 text-amber-300"
                    : "bg-quantum-turquoise/10 border-quantum-turquoise/40 text-quantum-turquoise"
                }`}
                title={
                  authUser
                    ? (membershipStatus === "active"
                        ? (language === "en" ? "Active Member" : "Aktivni Član")
                        : (language === "en" ? "Pending Approval" : "Na Čekanju"))
                    : (language === "en" ? "Guest / Click to Portal" : "Gost / Kliknite za Portal")
                }
              >
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-400" : "bg-amber-400") : "bg-quantum-turquoise"
                  }`}></span>
                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                    membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-500" : "bg-amber-500") : "bg-quantum-turquoise"
                  }`}></span>
                </span>
                <span className="font-bold text-[8px]">
                  {membershipLoading
                    ? "..."
                    : authUser
                    ? (membershipStatus === "active" ? "ACTIVE" : "PENDING")
                    : "GUEST"}
                </span>
              </button>

              {/* Hamburger Menu Toggle Button (Mobile) */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-1.5 border border-pleiadian-blue/40 rounded text-celestial-light hover:text-white hover:bg-pleiadian-blue/10 transition-all cursor-pointer flex items-center justify-center shrink-0"
                aria-label="Toggle Navigation Menu"
                id="mobile-menu-toggle-btn"
              >
                {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Row 2 (Desktop): Holographic Telemetry Indicator + Controls */}
          <div className="hidden md:flex items-center justify-between border-t border-pleiadian-blue/10 pt-2 text-xs font-mono">
            {/* Real-time telemetry log indicator */}
            <div className="flex items-center gap-2.5 text-[9px] text-pleiadian-blue/80 uppercase tracking-widest select-none">
              <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-pulse"></span>
              <span>COSMIC TELEMETRY: INTEGRATED</span>
              <span className="opacity-30">//</span>
              <span>M45 SECTOR LOCK</span>
              <span className="opacity-30">//</span>
              <span className="text-quantum-turquoise">ONLINE</span>
            </div>

            {/* Theme, Q-Jump, Membership Status and Language Switcher */}
            <div className="flex items-center gap-3">
              {/* Membership Status Badge */}
              <button
                onClick={() => scrollToSection("member-gate-section")}
                className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-wider transition-all cursor-pointer select-none shrink-0 ${
                  membershipLoading
                    ? "bg-cosmic-black/60 border-gray-700 text-gray-400"
                    : authUser
                    ? membershipStatus === "active"
                      ? "bg-emerald-950/80 border-emerald-500/60 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.3)] hover:border-emerald-400 hover:bg-emerald-900/90"
                      : "bg-amber-950/80 border-amber-500/60 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.3)] hover:border-amber-400 hover:bg-amber-900/90"
                    : "bg-quantum-turquoise/10 border-quantum-turquoise/40 text-quantum-turquoise hover:bg-quantum-turquoise/20 hover:border-quantum-turquoise"
                }`}
                title={
                  authUser
                    ? (membershipStatus === "active"
                        ? (language === "en" ? "Membership Status: Active Member (Click to manage)" : "Status Članstva: Aktivni Član (Kliknite za pregled)")
                        : (language === "en" ? "Membership Status: Pending Approval (Click to manage)" : "Status Članstva: Na Čekanju Odobrenja (Kliknite za pregled)"))
                    : (language === "en" ? "Member Portal: Click to Access / Register" : "Članski Portal: Kliknite za Pristup / Registraciju")
                }
              >
                <span className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-400" : "bg-amber-400") : "bg-quantum-turquoise"
                  }`}></span>
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${
                    membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-500" : "bg-amber-500") : "bg-quantum-turquoise"
                  }`}></span>
                </span>

                {authUser ? (
                  membershipStatus === "active" ? (
                    <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0" />
                  ) : (
                    <Clock className="w-3 h-3 text-amber-400 shrink-0" />
                  )
                ) : (
                  <UserCheck className="w-3 h-3 text-quantum-turquoise shrink-0" />
                )}

                <span className="font-bold">
                  {membershipLoading
                    ? (language === "en" ? "MEMBER: ..." : "ČLAN: ...")
                    : authUser
                    ? (membershipStatus === "active"
                        ? (language === "en" ? "MEMBER: ACTIVE" : "ČLAN: AKTIVAN")
                        : (language === "en" ? "MEMBER: PENDING" : "ČLAN: NA ČEKANJU"))
                    : (language === "en" ? "MEMBER: GUEST" : "ČLAN: GOST")}
                </span>
              </button>

              {/* Theme Selector */}
              <div className="flex items-center gap-2 pr-3 border-r border-pleiadian-blue/15">
                <Palette className="w-3.5 h-3.5 text-quantum-turquoise animate-pulse" />
                <span className="text-[9px] uppercase tracking-widest text-celestial-light/60">
                  {language === "en" ? "THEME:" : "ESTETIKA:"}
                </span>
                <div className="flex items-center gap-2 bg-cosmic-black/60 p-0.5 rounded-full border border-pleiadian-blue/25">
                  {COSMIC_THEMES.map((theme) => {
                    const isActive = activeThemeId === theme.id;
                    let gradientClass = "";
                    let glowColor = "";
                    if (theme.id === "nebula-deep-blue") {
                      gradientClass = "from-[#4A90E2] to-[#00D4FF]";
                      glowColor = "shadow-[0_0_10px_rgba(0,212,255,0.4)]";
                    } else if (theme.id === "crystalline-gold") {
                      gradientClass = "from-[#E6A119] to-[#FFD27F]";
                      glowColor = "shadow-[0_0_10px_rgba(230,161,25,0.4)]";
                    } else if (theme.id === "high-voltage-violet") {
                      gradientClass = "from-[#A67CFF] to-[#EC4899]";
                      glowColor = "shadow-[0_0_10px_rgba(166,124,255,0.4)]";
                    }
                    return (
                      <button
                        key={theme.id}
                        onClick={() => setActiveThemeId(theme.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer relative"
                        title={language === "en" ? theme.nameEn : theme.nameHr}
                        aria-label={language === "en" ? `Switch to theme ${theme.nameEn}` : `Promijeni temu na ${theme.nameHr}`}
                      >
                        <span className={`w-3 h-3 rounded-full bg-gradient-to-br ${gradientClass} ${isActive ? 'scale-110 border border-white ring-1 ring-quantum-turquoise' : 'hover:scale-110 opacity-60 hover:opacity-100'} transition-all ${glowColor}`} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* High-visibility Member Access CTA button next to theme toggle (visible for non-active members) */}
              {membershipStatus !== "active" && (
                <button
                  onClick={() => scrollToSection("member-gate-section")}
                  className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-golden-resonance via-amber-400 to-golden-resonance hover:from-amber-400 hover:to-amber-300 text-cosmic-black font-mono text-[10px] font-extrabold uppercase tracking-widest rounded-lg shadow-[0_0_12px_rgba(255,210,127,0.35)] hover:shadow-[0_0_20px_rgba(255,210,127,0.65)] transition-all transform hover:scale-105 active:scale-95 cursor-pointer shrink-0"
                  title={language === "en" ? "Member Access & Registration Portal" : "Portal za Člansku Autentifikaciju i Registraciju"}
                  id="header-member-access-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 text-cosmic-black fill-current shrink-0" />
                  <span>{language === "en" ? "MEMBER ACCESS" : "ČLANSKI PRISTUP"}</span>
                </button>
              )}

              {/* Q-Jump */}
              <button
                onClick={() => {
                  const event = new KeyboardEvent("keydown", { key: "k" });
                  window.dispatchEvent(event);
                }}
                className="flex items-center gap-1.5 px-2 py-0.5 border border-quantum-turquoise/40 hover:border-quantum-turquoise/80 rounded text-[9px] tracking-wider text-quantum-turquoise hover:bg-quantum-turquoise/10 transition-all cursor-pointer font-mono shrink-0 uppercase"
                title={language === "en" ? "Press 'K' to Quick-Jump" : "Pritisnite 'K' za Brzi Skok"}
              >
                <span>Q-JUMP</span>
                <kbd className="bg-quantum-turquoise/15 px-1 py-0.2 rounded font-mono font-bold text-[8px]">K</kbd>
              </button>

              {/* Language Switcher with anti-auto-translate protection */}
              <button
                onClick={() => setLanguage(language === "en" ? "hr" : "en")}
                translate="no"
                className="notranslate px-2.5 py-0.5 border border-pleiadian-blue/40 rounded text-[9px] tracking-wider text-celestial-light hover:bg-pleiadian-blue/10 transition-all cursor-pointer font-mono shrink-0 flex items-center gap-1 shadow-sm"
                title={language === "en" ? "Prebaci na Hrvatski (HR)" : "Switch to English (EN)"}
                aria-label={language === "en" ? "Switch to Croatian" : "Switch to English"}
              >
                <span translate="no" className="notranslate flex items-center gap-1">
                  <span className={language === "hr" ? "text-quantum-turquoise font-bold" : "text-gray-400 font-normal hover:text-white"}>HR</span>
                  <span className="text-gray-600">/</span>
                  <span className={language === "en" ? "text-quantum-turquoise font-bold" : "text-gray-400 font-normal hover:text-white"}>EN</span>
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Dropdown */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden w-full bg-cosmic-black/95 backdrop-blur-lg border-b border-pleiadian-blue/20 overflow-hidden relative z-30"
              id="mobile-navigation-dropdown"
            >
              <div className="px-6 py-4 flex flex-col gap-2 font-mono text-xs text-gray-300">
                {/* Mobile Membership Status Card inside dropdown */}
                <div className="mb-2 p-2.5 rounded-lg border bg-cosmic-black/80 flex items-center justify-between font-mono text-xs border-pleiadian-blue/20">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                        membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-400" : "bg-amber-400") : "bg-quantum-turquoise"
                      }`}></span>
                      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                        membershipLoading ? "bg-gray-400" : authUser ? (membershipStatus === "active" ? "bg-emerald-500" : "bg-amber-500") : "bg-quantum-turquoise"
                      }`}></span>
                    </span>
                    <div>
                      <div className="text-[9px] text-gray-400 uppercase tracking-widest">
                        {language === "en" ? "MEMBERSHIP STATUS" : "STATUS ČLANSTVA"}
                      </div>
                      <div className={`font-bold text-[11px] ${
                        membershipLoading
                          ? "text-gray-400"
                          : authUser
                          ? membershipStatus === "active"
                            ? "text-emerald-400"
                            : "text-amber-400"
                          : "text-quantum-turquoise"
                      }`}>
                        {membershipLoading
                          ? (language === "en" ? "CHECKING..." : "PROVJERAVANJE...")
                          : authUser
                          ? (membershipStatus === "active"
                              ? (language === "en" ? "ACTIVE MEMBER" : "AKTIVNI ČLAN")
                              : (language === "en" ? "PENDING APPROVAL" : "NA ČEKANJU ODOBRENJA"))
                          : (language === "en" ? "GUEST / UNREGISTERED" : "GOST / NEREGISTRIRAN")}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => scrollToSection("member-gate-section")}
                    className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider rounded border border-quantum-turquoise/40 text-quantum-turquoise bg-quantum-turquoise/10 hover:bg-quantum-turquoise/20 transition-all cursor-pointer shrink-0"
                  >
                    {authUser ? (language === "en" ? "PORTAL" : "PORTAL") : (language === "en" ? "SIGN IN" : "PRIJAVA")}
                  </button>
                </div>

                {/* 9 section buttons for mobile */}
                <a
                  href="/intro"
                  onClick={(e) => { e.preventDefault(); scrollToSection("intro-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "1. Introduction" : "1. Uvod"}
                </a>
                <a
                  href="/history"
                  onClick={(e) => { e.preventDefault(); scrollToSection("history-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "2. History" : "2. Povijest"}
                </a>
                <a
                  href="/clans"
                  onClick={(e) => { e.preventDefault(); scrollToSection("clans-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "3. Clans" : "3. Klanovi"}
                </a>
                <a
                  href="/technology"
                  onClick={(e) => { e.preventDefault(); scrollToSection("tech-desc-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "4. Technology" : "4. Tehnologija"}
                </a>
                <a
                  href="/contact"
                  onClick={(e) => { e.preventDefault(); scrollToSection("humanity-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "5. Contact Lineage" : "5. Zemaljski Kontakt"}
                </a>
                <a
                  href="/cosmology"
                  onClick={(e) => { e.preventDefault(); scrollToSection("comparison-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "6. Lyra–Orion Catalyst" : "6. Rat Lyra–Orion"}
                </a>
                <a
                  href="/evolution"
                  onClick={(e) => { e.preventDefault(); scrollToSection("evolution-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer block"
                >
                  {language === "en" ? "7. Ascensional Metaphysics" : "7. Metafizika Uzašašća"}
                </a>
                <a
                  href="/star-map"
                  onClick={(e) => { e.preventDefault(); scrollToSection("interactive-portal"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-semibold text-quantum-turquoise block"
                >
                  {language === "en" ? "8. Interactive Star Map" : "8. Interaktivna Karta"}
                </a>
                <a
                  href="/library"
                  onClick={(e) => { e.preventDefault(); scrollToSection("library-section"); }}
                  className="w-full text-left py-2 border-b border-pleiadian-blue/10 uppercase tracking-wider hover:text-quantum-turquoise transition-colors cursor-pointer font-bold text-mystic-purple block"
                >
                  {language === "en" ? "★ Pleiades Library" : "★ Knjižnica Plejada"}
                </a>

                {/* Mobile controls row */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-pleiadian-blue/15 mt-2">
                  <div className="flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5 text-quantum-turquoise" />
                    <div className="flex items-center gap-2 bg-cosmic-black/60 p-0.5 rounded-full border border-pleiadian-blue/25">
                      {COSMIC_THEMES.map((theme) => {
                        const isActive = activeThemeId === theme.id;
                        let gradientClass = "";
                        if (theme.id === "nebula-deep-blue") {
                          gradientClass = "from-[#4A90E2] to-[#00D4FF]";
                        } else if (theme.id === "crystalline-gold") {
                          gradientClass = "from-[#E6A119] to-[#FFD27F]";
                        } else if (theme.id === "high-voltage-violet") {
                          gradientClass = "from-[#A67CFF] to-[#EC4899]";
                        }
                        return (
                          <button
                            key={theme.id}
                            onClick={() => setActiveThemeId(theme.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer relative"
                            aria-label={language === "en" ? `Switch to theme ${theme.nameEn}` : `Promijeni temu na ${theme.nameHr}`}
                          >
                            <span className={`w-3 h-3 rounded-full bg-gradient-to-br ${gradientClass} ${isActive ? 'scale-110 ring-1 ring-quantum-turquoise' : 'opacity-60'} transition-all`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      const event = new KeyboardEvent("keydown", { key: "k" });
                      window.dispatchEvent(event);
                    }}
                    className="px-2 py-1 border border-quantum-turquoise/40 rounded text-[10px] tracking-wider text-quantum-turquoise font-mono uppercase"
                  >
                    Q-JUMP
                  </button>

                  <button
                    onClick={() => setLanguage(language === "en" ? "hr" : "en")}
                    translate="no"
                    className="notranslate px-2.5 py-1 border border-pleiadian-blue/40 rounded text-[10px] tracking-wider text-celestial-light font-mono flex items-center gap-1"
                    title={language === "en" ? "Prebaci na Hrvatski (HR)" : "Switch to English (EN)"}
                  >
                    <span translate="no" className="notranslate flex items-center gap-1">
                      <span className={language === "hr" ? "text-quantum-turquoise font-bold" : "text-gray-400 font-normal"}>HR</span>
                      <span className="text-gray-600">/</span>
                      <span className={language === "en" ? "text-quantum-turquoise font-bold" : "text-gray-400 font-normal"}>EN</span>
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* HERO SECTION - FULL SCREEN WIDTH */}
      <section id="hero-section" className="relative w-full overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center bg-cosmic-black border-b border-pleiadian-blue/15 pt-28 md:pt-32 pb-16 md:pb-24">
        {/* Background image filled across the whole hero section */}
        <div 
          onContextMenu={(e) => e.preventDefault()}
          className="absolute inset-0 pointer-events-none overflow-hidden select-none"
        >
          <AnimatePresence initial={false}>
            <motion.img
              key={heroImageIndex}
              src={heroImages[heroImageIndex]}
              alt="Pleiadian Background"
              referrerPolicy="no-referrer"
              fetchPriority="high"
              loading="eager"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.88 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 3.0, ease: "easeInOut" }}
              style={{ y, scale: 1.15 }}
              className="absolute inset-0 w-full h-full object-cover object-center lg:object-right-top"
            />
          </AnimatePresence>
          {/* Subtle repeating security watermarks diagonally tiled */}
          <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 p-8 md:p-16 pointer-events-none z-10 overflow-hidden opacity-[0.03] select-none">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="flex items-center justify-center -rotate-12 text-[9px] md:text-[11px] font-mono tracking-[0.25em] text-quantum-turquoise font-semibold whitespace-nowrap">
                PLEIADES-M45.COM
              </div>
            ))}
          </div>

          {/* Corner Watermark */}
          <div className="absolute top-24 right-4 z-20 pointer-events-none select-none px-2.5 py-1.5 bg-cosmic-black/70 backdrop-blur-sm rounded border border-quantum-turquoise/10 flex items-center gap-1 opacity-40">
            <span className="text-[8px] font-mono tracking-widest text-quantum-turquoise">BACKGROUND FEED // PLEIADES-M45.COM</span>
          </div>

          {/* Subtle gradient edges so it flows beautifully into header and footer black, while remaining 85-90% visible */}
          <div className="absolute inset-0 bg-gradient-to-r from-cosmic-black/85 via-cosmic-black/40 to-transparent z-1"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black/70 via-transparent to-transparent z-1"></div>
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cosmic-black/70 to-transparent z-1"></div>
        </div>

        {/* Content aligned to the 7xl grid width for consistency */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pleiadian-blue/20 border border-pleiadian-blue/40 backdrop-blur-sm shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-quantum-turquoise" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#93C5FD]">
                {language === "en" ? "Multi-Dimensional Cosmology Module" : "Višedimenzionalni kozmološki modul"}
              </span>
            </div>
            
            <div className="space-y-2">
              <span className="text-golden-resonance font-serif italic text-lg md:text-xl tracking-wide block drop-shadow-md">
                {language === "en" ? "The Seven Sisters" : "Sedam Sestara"}
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight text-white font-display drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]" id="main-hero-h1">
                {t.heroTitle}
              </h1>
            </div>

            <div className="text-[#E8EEF7] text-lg max-w-xl leading-relaxed font-light font-sans drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
              {parseTextWithGlossary(t.heroSub, language, handleOpenGlossary)}
            </div>

            {/* CTA Buttons - Immersive Hero Navigation */}
            <div className="flex flex-wrap gap-4 pt-4" id="hero-cta-buttons">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setIsLibrarySubpageOpen(true);
                  if (typeof window !== "undefined") {
                    window.history.pushState({}, "", "/pleiades-library");
                  }
                }}
                className="flex items-center gap-2.5 py-3.5 px-8 bg-gradient-to-r from-pleiadian-blue to-quantum-turquoise text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_25px_rgba(0,212,255,0.4)] hover:shadow-[0_0_35px_rgba(0,212,255,0.7)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                id="hero-btn-pleiadian-archive"
              >
                <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                <span>{language === "en" ? "PLEIADIAN ARCHIVE" : "PLEJADSKA ARHIVA"}</span>
                <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setIsChroniclesOpen(true);
                  if (typeof window !== "undefined") {
                    window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
                  }
                }}
                className="flex items-center gap-2.5 py-3.5 px-8 border border-golden-resonance/50 bg-gradient-to-r from-[#1c1407]/90 via-[#2a1d08]/90 to-[#1c1407]/90 hover:from-[#35250a] hover:to-[#221706] text-golden-resonance font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(255,210,127,0.25)] hover:shadow-[0_0_30px_rgba(255,210,127,0.5)] transition-all cursor-pointer backdrop-blur-md group hover:border-golden-resonance"
                id="hero-btn-lyra-chronicles"
              >
                <Sparkles className="w-4 h-4 text-golden-resonance group-hover:scale-110 transition-transform" />
                <span>{language === "en" ? "LYRA CHRONICLES" : "KRONIKE LYRE"}</span>
                <ArrowUpRight className="w-3.5 h-3.5 text-golden-resonance/60 group-hover:text-golden-resonance group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Hero Background Rotation Indicators */}
        <div className="absolute bottom-5 right-6 md:right-12 z-20 flex items-center gap-2 bg-cosmic-black/70 backdrop-blur-md px-3 py-1.5 rounded-full border border-quantum-turquoise/20 select-none shadow-[0_4px_16px_rgba(0,0,0,0.6)]">
          <div className="flex items-center gap-1.5">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setHeroImageIndex(idx)}
                title={
                  idx === 0 
                    ? (language === "en" ? "Pleiadians on a Terrace" : "Plejade na terasi")
                    : idx === 1 
                    ? (language === "en" ? "Pleiadian Prophecy of the Silent Ark's Return" : "Plejadsko proročanstvo povratka Tihe Arke")
                    : (language === "en" ? "Pleiades M45 Cluster" : "Zvjezdano jato Plejade M45")
                }
                aria-label={`Hero background slide ${idx + 1}`}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  heroImageIndex === idx
                    ? "w-6 h-1.5 bg-quantum-turquoise shadow-[0_0_10px_rgba(0,212,255,0.9)]"
                    : "w-1.5 h-1.5 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
          <span className="text-[9px] font-mono text-quantum-turquoise/80 ml-1">
            0{heroImageIndex + 1}/0{heroImages.length}
          </span>
        </div>
      </section>

      {/* Main Container */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-12 space-y-24">

        {/* SECTION 1: Tko su Plejadani? (Uvod) */}
        <section id="intro-section" className="bg-cosmic-indigo/30 backdrop-blur-md border-l-4 border-pleiadian-blue rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/20 inline-block">
                  {language === "en" ? "Section 1: Identity" : "Sekcija 1: Identitet"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec1Title}
                </h2>
                <div className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(t.sec1Intro, language, handleOpenGlossary)}
                </div>
                <div className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(
                    language === "en" 
                      ? "The Pleiadian archetype is a deeply woven cosmological pattern affecting Earth's scientific coordinates and metaphysical evolution. Unveil the astronomical geometry of Messier 45, the ancient global mythologies from Greece to Mayan calendars, and the spiritual 5D frequency templates."
                      : "Plejadanski arhetip duboko je utkani kozmološki uzorak koji utječe na znanstvene koordinate Zemlje i metafizičku evoluciju. Otkrijte astronomsku geometriju Messiera 45, drevne globalne mitologije od Grčke do majanskih kalendara i duhovne 5D frekvencijske predloške.",
                    language,
                    handleOpenGlossary
                  )}
                </div>
              </div>

              {/* Highlights for Homepage visual flow */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "M45 Blue Sun Cluster" : "M45 Plavo Zvjezdano Jato"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "444 Light-years distance" : "Udaljenost od 444 svjetlosne godine"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "Global Mythology" : "Globalna Mitologija"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Greek, Mayan & Subaru roots" : "Grčki, majanski i Subaru korijeni"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "5D Coherence" : "5D Koherencija"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Heart-centered telepathic net" : "Srčano usmjerena telepatska mreža"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Cellular Activation" : "Stanična Aktivacija"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Resonance triggers within starseeds" : "Rezonantni okidači u zvjezdanom sjemenu"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(7)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-pleiadian-blue to-quantum-turquoise text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(0,212,255,0.35)] hover:shadow-[0_0_30px_rgba(0,212,255,0.6)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Full Identity Profile" : "Istraži Cijeli Profil Identiteta"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Moved current small image to Section 1: Identity */}
            <div className="lg:col-span-5 w-full relative rounded-xl border border-pleiadian-blue/20 overflow-hidden shadow-xl group flex flex-col items-center justify-center bg-cosmic-black self-stretch min-h-[300px]">
              <div 
                onContextMenu={(e) => e.preventDefault()}
                className="relative w-full h-full min-h-[220px] lg:h-full flex-grow select-none"
              >
                <img
                  src={heroBannerImg}
                  alt="Pleiades M45 Star Cluster"
                  width="1200"
                  height="800"
                  loading="eager"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-1000"
                />
                
                {/* Subtle repeating security watermarks diagonally tiled */}
                <div className="absolute inset-0 grid grid-cols-2 gap-6 p-4 pointer-events-none z-10 overflow-hidden opacity-[0.04] select-none">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex items-center justify-center -rotate-12 text-[8px] font-mono tracking-[0.2em] text-quantum-turquoise font-semibold whitespace-nowrap">
                      PLEIADES-M45.COM
                    </div>
                  ))}
                </div>

                {/* Corner Watermark */}
                <div className="absolute top-4 right-4 z-20 pointer-events-none select-none px-2.5 py-1.5 bg-cosmic-black/80 backdrop-blur-sm rounded border border-quantum-turquoise/20 flex items-center gap-1 opacity-50">
                  <span className="text-[7px] font-mono tracking-widest text-quantum-turquoise">SECURE RECORD // PLEIADES-M45.COM</span>
                </div>

                {/* Holographic matrix geometry representation overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black via-cosmic-black/10 to-transparent z-1"></div>
                
                {/* Holographic lines overlay */}
                <div className="absolute inset-0 pointer-events-none border border-pleiadian-blue/10 m-3 rounded-lg z-5"></div>
              </div>

              <div className="absolute bottom-4 left-4 right-4 bg-cosmic-black/70 backdrop-blur-md border border-pleiadian-blue/20 rounded-xl p-3 flex justify-between items-center z-10">
                <div className="flex items-center gap-2">
                  <Compass className="w-4 h-4 text-quantum-turquoise animate-spin" style={{ animationDuration: "12s" }} />
                  <span className="text-[10px] font-mono tracking-wider text-celestial-light uppercase">
                    M45 SPECTRAL ARCHIVE
                  </span>
                </div>
                <span className="text-[9px] font-mono text-gray-400">CLASS B GIANT</span>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: Povijest Plejada kroz kulture */}
        <section id="history-section" className="bg-cosmic-indigo/20 backdrop-blur-md border-l-4 border-mystic-purple rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-mystic-purple bg-mystic-purple/10 px-3 py-1 rounded-full border border-mystic-purple/20 inline-block">
                  {language === "en" ? "Section 2: Cultural Lineage" : "Sekcija 2: Kulturna Loza"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec2Title}
                </h2>
                <div className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(t.sec2Intro, language, handleOpenGlossary)}
                </div>
                <div className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(
                    language === "en"
                      ? "Throughout terrestrial human epochs, the Seven Sisters have been recognized as celestial anchors of time, agriculture, and high mythology. From European cave paintings to Mesoamerican calendars, our ancestors encoded their presence across the world."
                      : "Tijekom zemaljskih ljudskih epoha, Sedam Sestara prepoznate su kao nebeska sidra vremena, poljoprivrede i visoke mitologije. Od europskih špiljskih slika do mezoameričkih kalendara, naši su preci kodirali njihovu prisutnost diljem svijeta.",
                    language,
                    handleOpenGlossary
                  )}
                </div>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "Prehistoric Cave Art" : "Prapovijesna Umjetnost"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Lascaux's 17k-year-old drawings" : "Slike iz Lascauxa stare 17.000 g."}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "Mesopotamian Mul-Mul" : "Mezopotamski Mul-Mul"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Sumerian star catalog anchors" : "Sidra sumerskog kataloga zvijezda"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "Mayan Tzolk'in Cycle" : "Majanski Ciklus Tzolk'in"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Calendars aligned to Pleiades" : "Kalendari usklađeni s Plejadama"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Aboriginal Songlines" : "Aboridžinske Pjesme Puta"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Ancient oral celestial navigation maps" : "Drevne usmene karte nebeske plovidbe"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(1)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-mystic-purple to-celestial-light text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(147,51,234,0.35)] hover:shadow-[0_0_30px_rgba(147,51,234,0.6)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Full Cultural Lineage" : "Istraži Cijelu Kulturnu Lozu"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Side visual box */}
            <div className="lg:col-span-5 bg-cosmic-black/80 border border-mystic-purple/20 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-2xl min-h-[220px]">
              <div>
                <span className="text-[10px] font-mono text-mystic-purple uppercase tracking-widest block mb-1">
                  HISTORICAL DATA RECORD
                </span>
                <h4 className="text-sm font-mono uppercase tracking-wider text-white font-bold mb-3">
                  {language === "en" ? "Seven Sisters Chronicles" : "Kronike Sedam Sestara"}
                </h4>
                <div className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(
                    language === "en"
                      ? "Almost every isolated ancient culture links the Pleiades with seven sisters fleeing a celestial pursuer. This global consensus suggests a shared root narrative predating continental migration."
                      : "Gotovo svaka izolirana drevna kultura povezuje Plejade sa sedam sestara koje bježe od nebeskog progonitelja. Ovaj globalni konsenzus sugerira zajednički korijenski narativ prije kontinentalne migracije.",
                    language,
                    handleOpenGlossary
                  )}
                </div>
                <div className="mt-4 p-3 bg-cosmic-indigo/30 border border-mystic-purple/20 rounded text-[10px] font-mono text-celestial-light">
                  COORDINATE: M45 HISTORICAL ARCHIVE // ONLINE
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: Plejadanska civilizacija (meta-razina) & Clan Archetypes */}
        <section id="clans-section" className="bg-cosmic-indigo/20 backdrop-blur-md border-l-4 border-quantum-turquoise rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden space-y-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/20 inline-block">
                  {language === "en" ? "Section 3: Galactic Society" : "Sekcija 3: Galaktičko Društvo"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec3Title}
                </h2>
                <div className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(t.sec3Intro, language, handleOpenGlossary)}
                </div>
                <div className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(
                    language === "en"
                      ? "Pleiadian galactic society operates without hierarchical command lines, relying on telepathic unity, non-local clan coordinates, and heart-centered resonance. Discover how decisions are made through dynamic harmonic councils."
                      : "Plejadansko galaktičko društvo djeluje bez hijerarhijskih zapovjednih linija, oslanjajući se na telepatsko jedinstvo, nelokalne klanove i koherenciju srca. Otkrijte kako se odluke donose kroz dinamična harmonijska vijeća.",
                    language,
                    handleOpenGlossary
                  )}
                </div>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "The Clan Network" : "Mreža Klanova"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Decentralized, non-local governance" : "Decentralizirano, nelokalno upravljanje"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "Ethics of the One" : "Etika Zakona Jednog"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Empathy where harm registers instantly" : "Empatija gdje se šteta bilježi trenutno"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "Harmonic Mentorship" : "Harmonijsko Mentorstvo"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Activating wisdom of younger worlds" : "Aktiviranje mudrosti mlađih svjetova"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Seven Star Archetypes" : "Sedam Zvjezdanih Arhetipova"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Distinct schools of galactic focus" : "Posebne škole galaktičkog fokusa"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(2)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-quantum-turquoise to-pleiadian-blue text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(0,212,255,0.35)] hover:shadow-[0_0_30px_rgba(0,212,255,0.6)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Society & Starlight Clans" : "Istraži Civilizaciju i Zvjezdane Klanove"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Side visual box */}
            <div className="lg:col-span-5 bg-cosmic-black/80 border border-quantum-turquoise/20 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-2xl min-h-[220px]">
              <div>
                <span className="text-[10px] font-mono text-quantum-turquoise uppercase tracking-widest block mb-1">
                  ROLE & DESTINY
                </span>
                <h4 className="text-sm font-mono uppercase tracking-wider text-white font-bold mb-3">
                  {t.sec3Sub5Title}
                </h4>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {t.sec3Sub5Text}
                </p>
                <div className="bg-cosmic-indigo/30 rounded border border-quantum-turquoise/20 p-4 mt-4 text-[11px] font-mono text-celestial-light italic leading-relaxed">
                  "Among galactic councils, the Pleiadian clans act as anchors of the Divine Feminine Ray — focusing on integration rather than domination."
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Clan Archetypes Selector Widget */}
          <ArchetypeSelector language={language} />
        </section>

        {/* SECTION 4: Tehnologija Plejadana */}
        <section id="tech-desc-section" className="bg-cosmic-indigo/20 backdrop-blur-md border-l-4 border-golden-resonance rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          {/* Background Image - 85% darkened */}
          <div 
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 pointer-events-none overflow-hidden z-0 select-none"
          >
            <img
              src={pleiadianTechImg}
              alt="Pleiadian Technology Background"
              width="1200"
              height="800"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ opacity: 0.15 }}
              referrerPolicy="no-referrer"
            />
            {/* Subtle repeating security watermarks diagonally tiled */}
            <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 p-8 md:p-16 pointer-events-none z-10 overflow-hidden opacity-[0.02] select-none">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className="flex items-center justify-center -rotate-12 text-[9px] md:text-[11px] font-mono tracking-[0.25em] text-quantum-turquoise font-semibold whitespace-nowrap">
                  PLEIADES-M45.COM
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-golden-resonance bg-golden-resonance/10 px-3 py-1 rounded-full border border-golden-resonance/20 inline-block">
                  {language === "en" ? "Section 4: Vibrational Physics" : "Sekcija 4: Vibracijska Fizika"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec4Title}
                </h2>
                <div className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(t.sec4Intro, language, handleOpenGlossary)}
                </div>
                <div className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {parseTextWithGlossary(
                    language === "en"
                      ? "A complete scientific-metaphysical synthesis where tools are organic, resonant, and operated directly by high-vibrational emotional coherence. Power is channeled through engineered quartz-crystalline structures and torsion field space foldings."
                      : "Potpuna znanstveno-metafizička sinteza u kojoj su alati organski, rezonantni i upravljani izravno visokom emocionalnom koherencijom. Snaga se kanalizira kroz kvarc-kristalne strukture i torzijsko savijanje prostora.",
                    language,
                    handleOpenGlossary
                  )}
                </div>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "Crystalline Amplifiers" : "Kristalna Pojačala"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Tapping zero-point quantum vacuums" : "Korištenje kvantnog vakuuma nulte točke"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "Conscious Bio-Ships" : "Svjesne Bio-Letjelice"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Grown organic crystalline alloys" : "Uzgajane organske kristalne legure"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "Torsion Fold Travel" : "Torzijska Putovanja"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Instant transition without relativity limits" : "Trenutni prijelaz bez ograničenja relativnosti"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Liquid Light Cities" : "Gradovi Tekuće Svhetlosti"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Structures as giant telluric wave lenses" : "Strukture kao divovske leće valova"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(3)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-golden-resonance to-amber-500 text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(245,158,11,0.35)] hover:shadow-[0_0_30px_rgba(245,158,11,0.6)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Quantum & Crystalline Science" : "Istraži Kvantnu i Kristalnu Znanost"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Side visual box */}
            <div className="lg:col-span-5 bg-cosmic-black/80 border border-golden-resonance/20 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-2xl min-h-[220px]">
              <div>
                <span className="text-[10px] font-mono text-golden-resonance uppercase tracking-widest block mb-1">
                  QUANTUM PHYSICS PARADIGM
                </span>
                <h4 className="text-sm font-mono uppercase tracking-wider text-white font-bold mb-3">
                  {language === "en" ? "Resonant vs. Brute Tech" : "Rezonantna vs. Gruba Tehnologija"}
                </h4>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "Where Earth uses combustion, binary computation, and brute physical force, Pleiadian science utilizes soft harmonics, crystalline grids, and direct consciousness interfacing."
                    : "Dok Zemlja koristi izgaranje, binarno računanje i grubu fizičku silu, plejadanska znanost koristi blage harmonike, kristalne mreže i izravno povezivanje sa sviješću."}
                </p>
                <div className="mt-4 p-3 bg-cosmic-indigo/30 border border-golden-resonance/20 rounded text-[10px] font-mono text-golden-resonance">
                  GRID MODULE M45-TECH // SYNERGETIC
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTER-SECTION STATS SCREEN & VISUAL EMBED */}
        <section className="w-full py-4 relative" id="pleiad-terrace-visual-widget">
          {/* Brutalist Frame Corner Markers */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-quantum-turquoise z-20"></div>
          <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-quantum-turquoise z-20"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-quantum-turquoise z-20"></div>
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-quantum-turquoise z-20"></div>
          
          {/* Outer Border Box */}
          <div className="border border-quantum-turquoise/40 bg-cosmic-black/90 p-1 md:p-2 relative overflow-hidden shadow-[0_0_35px_rgba(0,212,255,0.15)]">
            
            {/* HUD Header bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-quantum-turquoise/30 px-4 py-2 bg-quantum-turquoise/5 text-[10px] font-mono text-quantum-turquoise tracking-widest gap-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                <span>SYSTEM: DIRECT OBSERVATION FEED // M45_SYS_LOCK</span>
              </div>
              <div className="flex items-center gap-4">
                <span>COORD: RA 03h 47m 24s / DEC +24°07'</span>
                <span className="hidden md:inline">SIGNAL: STABLE [100%]</span>
              </div>
            </div>

            {/* Cinematic Wide Image Section */}
            <div 
              onContextMenu={(e) => e.preventDefault()}
              className="relative w-full border-b border-quantum-turquoise/30 overflow-hidden group select-none"
            >
              {/* Scanlines overlay effect */}
              <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,212,255,0.05)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none z-10 opacity-60"></div>
              {/* Vignette & tint overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black via-transparent to-transparent opacity-70 z-10 pointer-events-none"></div>
              
              {/* Subtle repeating security watermarks diagonally tiled */}
              <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 p-8 md:p-16 pointer-events-none z-15 overflow-hidden opacity-[0.05] select-none">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-center -rotate-12 text-[9px] md:text-[11px] font-mono tracking-[0.25em] text-quantum-turquoise font-semibold whitespace-nowrap">
                    PLEIADES-M45.COM
                  </div>
                ))}
              </div>

              {/* Corner Watermark */}
              <div className="absolute top-4 right-4 z-20 pointer-events-none select-none px-2.5 py-1.5 bg-cosmic-black/70 backdrop-blur-sm rounded border border-quantum-turquoise/20 flex items-center gap-1.5 opacity-50">
                <span className="text-[8px] md:text-[9px] font-mono tracking-widest text-quantum-turquoise">SECURE FEED // PLEIADES-M45.COM</span>
              </div>
              
              <img 
                src={pleiadWomanImg} 
                alt="Pleiadian woman looking towards the starlight cluster" 
                width="1200"
                height="800"
                loading="lazy"
                decoding="async"
                className="w-full h-auto aspect-video object-cover block grayscale-[15%] group-hover:scale-102 transition-transform duration-1000"
                referrerPolicy="no-referrer"
              />

              {/* Dynamic Stellar Navigation overlay */}
              <StellarNavigation language={language} />

              {/* Subtitle overlaid on the cinematic image at bottom-left - Hidden on mobile as requested */}
              <div className="hidden md:block absolute bottom-4 left-4 right-4 md:left-6 md:right-6 z-20 bg-cosmic-black/85 border border-quantum-turquoise/30 p-4 backdrop-blur-sm max-w-3xl">
                <p className="text-[10px] font-mono text-quantum-turquoise uppercase tracking-widest mb-1 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-ping"></span>
                  {language === "en" ? "OBSERVATION LOG: TERRACE RETREAT" : "DNEVNIK ZAPAŽANJA: TERASA ZA MEDITACIJU"}
                </p>
                <p className="text-[11px] sm:text-xs text-gray-300 leading-relaxed font-sans">
                  {language === "en" 
                    ? "A Pleiadian observer stands upon a crystalline terrace, her gaze fixed toward the celestial heavens and the vibrant M45 star cluster, establishing high-harmonic alignment."
                    : "Promatračica s Plejada stoji na kristalnoj terasi, a pogled joj je usmjeren prema zvjezdanom nebu i blistavom jatu Plejada (M45), uspostavljajući usklađenost s visokim harmonicima."}
                </p>
              </div>
            </div>

            {/* Data Readouts Row - Collapsible Holographic Panel */}
            <div className="border-t border-quantum-turquoise/20 bg-cosmic-black/40">
              {/* CTA Trigger Bar */}
              <div className="p-4 md:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-cosmic-black via-cosmic-black/90 to-cosmic-black/85">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${isTelemetryOpen ? "bg-quantum-turquoise animate-ping" : "bg-gray-600"}`}></div>
                  <div className="text-left">
                    <span className="text-[9px] text-pleiadian-blue uppercase tracking-widest block font-bold font-mono">SECURE TELEMETRY LINK</span>
                    <h4 className="text-xs md:text-sm text-white font-bold tracking-widest uppercase font-mono">
                      {language === "en" ? "PLANETARY & SPECIES ANALYSIS" : "ANALIZA PLANETA I VRSTE"}
                    </h4>
                  </div>
                </div>

                <button
                  onClick={() => setIsTelemetryOpen(!isTelemetryOpen)}
                  className={`w-full sm:w-auto px-5 py-2 font-mono text-[10px] md:text-xs tracking-wider uppercase border rounded-md cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 shadow-[0_0_12px_rgba(0,212,255,0.05)] ${
                    isTelemetryOpen
                      ? "bg-quantum-turquoise/20 text-quantum-turquoise border-quantum-turquoise hover:bg-quantum-turquoise/30"
                      : "bg-cosmic-black text-celestial-light border-quantum-turquoise/40 hover:border-quantum-turquoise hover:text-quantum-turquoise hover:bg-quantum-turquoise/10"
                  }`}
                >
                  <Database className={`w-3.5 h-3.5 ${isTelemetryOpen ? "animate-spin" : ""}`} />
                  {isTelemetryOpen
                    ? (language === "en" ? "DISCONNECT HOLO-MATRIX" : "ZATVORI HOLO-MATRICU")
                    : (language === "en" ? "LOAD TELEMETRY DATABASE" : "UČITAJ TELEMETRIJSKU BAZU")
                  }
                </button>
              </div>

              {/* Collapsible Holographic Screen Section */}
              <AnimatePresence>
                {isTelemetryOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 md:p-8 font-mono text-xs text-gray-300 bg-quantum-turquoise/[0.01] border-t border-b border-quantum-turquoise/25 relative">
                      
                      {/* Holographic Glowing grid overlay */}
                      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,212,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,212,255,0.02)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none opacity-60"></div>
                      
                      {/* Interactive Diagonal Scanline Layer */}
                      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(0,212,255,0.04)_50%)] bg-[size:100%_6px] pointer-events-none opacity-80"></div>
                      
                      {/* Cybernetic Corner Brackets */}
                      <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-quantum-turquoise/50 pointer-events-none"></div>
                      <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-quantum-turquoise/50 pointer-events-none"></div>
                      <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-quantum-turquoise/50 pointer-events-none"></div>
                      <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-quantum-turquoise/50 pointer-events-none"></div>

                      {/* Floating holographic ambient light pulse */}
                      <div className="absolute -top-10 left-1/4 w-96 h-24 bg-quantum-turquoise/5 rounded-full blur-[60px] pointer-events-none animate-pulse"></div>

                      {/* Header Inside Holographic Matrix */}
                      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-quantum-turquoise/20">
                        <div className="space-y-1">
                          <span className="text-[9px] text-quantum-turquoise tracking-widest block font-bold animate-pulse">[ESTABLISHING SECURE PROTOCOL // DECRYPTED_READOUT]</span>
                          <h4 className="text-sm text-white font-bold tracking-widest uppercase">
                            {language === "en" ? "VIRTUAL TELEMETRY INTERFACE" : "SUČELJE VIRTUALNE TELEMETRIJE"}
                          </h4>
                        </div>
                        <div className="text-[9px] text-quantum-turquoise bg-quantum-turquoise/10 border border-quantum-turquoise/30 px-3 py-1 rounded-sm shadow-[0_0_10px_rgba(0,212,255,0.2)]">
                          {language === "en" ? "STATUS: HOLO_STREAM_STABLE" : "STATUS: HOLO_STRUJANJE_STABILNO"}
                        </div>
                      </div>

                      {/* Grid with 2 columns below the wide image */}
                      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                        
                        {/* Planet Profile Stats (HUD style) */}
                        <div className="space-y-4 p-4 bg-cosmic-black/80 border border-quantum-turquoise/15 rounded backdrop-blur-sm shadow-[inset_0_0_15px_rgba(0,212,255,0.03)]">
                          <div className="text-[10px] text-quantum-turquoise border-b border-quantum-turquoise/20 pb-1.5 font-bold uppercase tracking-wider flex items-center justify-between">
                            <span>{language === "en" ? "[01] PRIMARY COGNITIVE WORLD" : "[01] PRIMARNI SVIJET"}</span>
                            <span className="text-[8px] opacity-60">SYSTEM_TAYGETA_M45</span>
                          </div>
                          <div className="space-y-2.5">
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Planet Name:" : "Naziv planeta:"}</span>
                              <span className="text-white font-bold tracking-wider">ERRA (Taygeta-A)</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Dimension / Density:" : "Dimenzija / Gustoća:"}</span>
                              <span className="text-quantum-turquoise font-bold">{language === "en" ? "5D - 6D Plasmic Reality" : "5D - 6D Plazmatska Stvarnost"}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Gravity / Mass:" : "Gravitacija / Masa:"}</span>
                              <span className="text-white">1.05G // 0.91 Earth Mass</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Atmosphere:" : "Atmosfera:"}</span>
                              <span className="text-white">N₂ (74%), O₂ (23%), Ar / Ether (3%)</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Surface Orbit:" : "Površinska orbita:"}</span>
                              <span className="text-white">365.2 Era-Cycles // 1.2M Pop.</span>
                            </div>
                          </div>
                        </div>

                        {/* Biological Species Stats (HUD style) */}
                        <div className="space-y-4 p-4 bg-cosmic-black/80 border border-pleiadian-blue/15 rounded backdrop-blur-sm shadow-[inset_0_0_15px_rgba(0,212,255,0.03)]">
                          <div className="text-[10px] text-pleiadian-blue border-b border-pleiadian-blue/20 pb-1.5 font-bold uppercase tracking-wider flex items-center justify-between">
                            <span>{language === "en" ? "[02] BIOLOGICAL TEMPLATE" : "[02] BIOLOŠKI PREDLOŽAK"}</span>
                            <span className="text-[8px] opacity-60">GENOME_REF_PL_009</span>
                          </div>
                          <div className="space-y-2.5">
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Taxonomy:" : "Taksonomija:"}</span>
                              <span className="text-white font-bold tracking-wider">{language === "en" ? "Ethereal Humanoid" : "Eterični humanoid"}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Height / Physical:" : "Visina / Građa:"}</span>
                              <span className="text-white">2.1m - 2.4m // Bioluminescent Cells</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Genetic Structure:" : "Genetska struktura:"}</span>
                              <span className="text-quantum-turquoise font-bold">{language === "en" ? "12-Strand Crystalline DNA" : "12-lančana Kristalna DNK"}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Lifespan:" : "Životni vijek:"}</span>
                              <span className="text-white">500 - 900 {language === "en" ? "Earth Years" : "Zemaljskih Godina"}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-gray-400">{language === "en" ? "Core Sensoria:" : "Osjetila:"}</span>
                              <span className="text-pleiadian-blue font-bold text-right">
                                {language === "en" ? "Telepathy, Empathy, Astral Projection" : "Telepatija, Empatija, Astralna Projekcija"}
                              </span>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Animated status bar footer */}
                      <div className="relative z-10 mt-8 pt-4 border-t border-quantum-turquoise/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-6 text-[9px] text-gray-500 font-mono">
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-pulse"></span>
                            <span className="text-quantum-turquoise">432Hz HARMONIC SYNC</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-mystic-purple animate-pulse"></span>
                            <span className="text-mystic-purple">RESONATING HOLO-SPECTRUM</span>
                          </div>
                        </div>
                        <div className="w-full sm:w-64 h-1 bg-white/5 rounded-full overflow-hidden relative">
                          <div className="absolute top-0 left-0 h-full w-[85%] bg-gradient-to-r from-quantum-turquoise via-pleiadian-blue to-mystic-purple rounded-full animate-pulse"></div>
                        </div>
                      </div>

                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* SECTION 5: Plejadani i ljudska civilizacija */}
        <section id="humanity-section" className="bg-cosmic-indigo/20 backdrop-blur-md border-l-4 border-celestial-light rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-celestial-light bg-celestial-light/10 px-3 py-1 rounded-full border border-celestial-light/20 inline-block">
                  {language === "en" ? "Section 5: Earth Contact" : "Sekcija 5: Zemaljski Kontakt"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec5Title}
                </h2>
                <p className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {t.sec5Intro}
                </p>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "A shared genetic and spiritual lineage aligning ancient monuments across Egypt, Mexico, and Native American Medicine wheels to the exact coordinates of Messier 45. Explore the modern psychology of Starseeds and the dormant DNA blueprints."
                    : "Zajednička genetska i duhovna loza koja usklađuje drevne spomenike diljem Egipta, Meksika i indijanskih medicinskih kotača s točnim koordinatama Messiera 45. Istražite modernu psihologiju Zvjezdanog sjemena."}
                </p>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Twelve-Strand DNA Keys" : "Dvanaestolančana DNK"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Activating dormant genetic blueprints" : "Aktiviranje uspavanih genetskih nacrta"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "Sacred Pyramid Shafts" : "Sveti Kanali Piramida"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Giza and Mayan temples pointed to Alcyone" : "Giza i hramovi Maja usmjereni na Alcyone"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "Starseed Psychology" : "Psihologija Zvjezdanog Sjemena"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Highly developed empathy & sensitivity" : "Visoko razvijena empatija i osjetljivost"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "The Future Mirror" : "Buduće Ogledalo"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Humanity's transition into planetary care" : "Tranzicija čovječanstva u brigu o planetu"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(4)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-celestial-light to-quantum-turquoise text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(186,230,253,0.35)] hover:shadow-[0_0_30px_rgba(186,230,253,0.6)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Terrestrial Contact Lineage" : "Istraži Zemaljski Kontakt i Sijanje"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Side visual box */}
            <div className="lg:col-span-5 bg-cosmic-black/80 border border-celestial-light/20 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-2xl min-h-[220px]">
              <div>
                <span className="text-[10px] font-mono text-celestial-light uppercase tracking-widest block mb-1">
                  TERRESTRIAL RESONANCE
                </span>
                <h4 className="text-sm font-mono uppercase tracking-wider text-white font-bold mb-3">
                  {language === "en" ? "The Call of Home" : "Poziv Doma"}
                </h4>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "Many people feel an unexplainable home-sickness when looking at the Pleiades. This deep-seated psychological magnetism is a soul-level recognition of a peaceful, cooperative state of being."
                    : "Mnogi ljudi osjećaju neobjašnjivu nostalgiju za domom gledajući u Plejade. Ovaj duboko ukorijenjeni psihološki magnetizam je prepoznavanje mirnog, suradničkog stanja postojanja."}
                </p>
                <div className="mt-4 p-3 bg-cosmic-indigo/30 border border-celestial-light/20 rounded text-[10px] font-mono text-celestial-light">
                  BLUEPRINT CORRELATION: 99.8% // ALIGNED
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: Rat Lyra-Orion & Kozmička Prekretnica */}
        <section id="comparison-section" className="border-l-4 md:border-l-8 border-red-500/80 rounded-r-2xl p-6 md:p-10 lg:p-12 shadow-[0_0_55px_rgba(239,68,68,0.15)] relative overflow-hidden transition-all duration-300">
          {/* Background Gradient Layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#120710]/98 via-[#0A0714]/98 to-[#18060B]/98 -z-20 pointer-events-none" />
          {/* Background image overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center pointer-events-none -z-10 opacity-15 mix-blend-luminosity"
            style={{ backgroundImage: `url("${lyranOrionWarImg}")` }}
          />
          {/* Cosmic atmospheric glows */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-[130px] pointer-events-none -z-10" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-500/10 rounded-full blur-[110px] pointer-events-none -z-10" />
          <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none -z-10"></div>
          
          <div className="relative z-10 space-y-8">
            {/* Header Block */}
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-mono uppercase tracking-widest shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                <Flame className="w-3.5 h-3.5 text-red-500 animate-pulse" />
                <span>
                  {language === "en" 
                    ? "Section 6: Galactic Turning Point Beyond Imagination" 
                    : "Sekcija 6: Galaktička Prekretnica Izvan Granica Mašte"}
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-ethereal-white leading-tight tracking-tight">
                {t.sec6Title}
              </h2>
              
              <p className="text-sm md:text-base text-gray-300 leading-relaxed font-sans font-light">
                {t.sec6Intro}
              </p>
            </div>

            {/* Visual Dual Force Field Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              
              {/* Left Faction: Lyra Sovereign Cradle */}
              <div className="lg:col-span-5 bg-gradient-to-b from-amber-950/20 via-cosmic-black/80 to-amber-950/10 border border-amber-500/30 rounded-xl p-5 md:p-6 relative overflow-hidden flex flex-col justify-between shadow-xl group hover:border-amber-400/50 transition-all">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none"></div>
                <div>
                  <div className="flex items-center justify-between mb-3 border-b border-amber-500/20 pb-2.5">
                    <span className="text-[10px] font-mono text-amber-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                      {language === "en" ? "LYRA // GOLDEN CRADLE" : "LYRA // ZLATNA KOLIJEVKA"}
                    </span>
                    <span className="text-[9px] font-mono text-amber-300/60 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                      APEX HARMONY
                    </span>
                  </div>
                  
                  <h3 className="text-base font-display font-semibold text-white mb-2">
                    {language === "en" ? "The Sacred Humanoid Genesis" : "Sveta Humanoidna Geneza"}
                  </h3>
                  
                  <p className="text-xs text-gray-300 leading-relaxed mb-4">
                    {language === "en"
                      ? "The original paradise where sovereign humanoid consciousness flourished in organic resonance, bio-empathy, and uninhibited creative expansion."
                      : "Izvorni raj u kojem je suverena humanoidna svijest cvjetala u organskoj rezonanciji, empatiji i neograničenoj kreativnoj ekspanziji."}
                  </p>

                  <div className="space-y-2 font-mono text-[11px]">
                    <div className="flex items-center gap-2 text-amber-300/90 bg-amber-950/20 px-2.5 py-1.5 rounded border border-amber-500/15">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
                      <span>{language === "en" ? "Energetic Frequency: High Heart Bio-Resonance" : "Energetska Frekvencija: Srčana Biorezonancija"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 bg-cosmic-black/40 px-2.5 py-1.5 rounded border border-white/5">
                      <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise" />
                      <span>{language === "en" ? "Legacy: Foundation of Pleiades & Sirius" : "Ostavština: Temelj Plejada i Siriusa"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Central Collision Axis Divider */}
              <div className="lg:col-span-2 flex flex-col justify-center items-center bg-gradient-to-b from-red-950/40 via-red-900/20 to-amber-950/40 border border-red-500/30 rounded-xl p-4 text-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-red-600/5 animate-pulse pointer-events-none" />
                <Flame className="w-8 h-8 text-red-500 animate-bounce mb-2" />
                <span className="text-[10px] font-mono text-red-400 font-bold uppercase tracking-wider block mb-1">
                  {language === "en" ? "COSMIC CATALYST" : "KOZMIČKI KATALIZATOR"}
                </span>
                <p className="text-[11px] text-gray-300 leading-tight font-sans">
                  {language === "en" 
                    ? "The collision that shattered golden worlds and reshaped the entire Galaxy."
                    : "Sudari koji su slomili zlatne svjetove i zauvijek izmijenili sudbinu Galaktike."}
                </p>
                <div className="mt-3 px-2 py-1 bg-red-950/60 border border-red-500/30 rounded text-[9px] font-mono text-red-300 tracking-widest uppercase">
                  SECTOR LYRA 01
                </div>
              </div>

              {/* Right Faction: Orion Imperial Duality */}
              <div className="lg:col-span-5 bg-gradient-to-b from-red-950/20 via-cosmic-black/80 to-red-950/10 border border-red-500/30 rounded-xl p-5 md:p-6 relative overflow-hidden flex flex-col justify-between shadow-xl group hover:border-red-400/50 transition-all">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-2xl pointer-events-none"></div>
                <div>
                  <div className="flex items-center justify-between mb-3 border-b border-red-500/20 pb-2.5">
                    <span className="text-[10px] font-mono text-red-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5 text-red-400" />
                      {language === "en" ? "ORION // POLAR DUALITY" : "ORION // POLARNA DUALNOST"}
                    </span>
                    <span className="text-[9px] font-mono text-red-300/60 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20">
                      STERN CONTROL
                    </span>
                  </div>
                  
                  <h3 className="text-base font-display font-semibold text-white mb-2">
                    {language === "en" ? "The Great Galactic Crucible" : "Veliko Galaktičko Raskršće"}
                  </h3>
                  
                  <p className="text-xs text-gray-300 leading-relaxed mb-4">
                    {language === "en"
                      ? "A rigid era of technological domination and intense polarity—forcing humanoid consciousness to transmute deep conflict into spiritual sovereignty."
                      : "Razdoblje strogog nadzora i intenzivne polarizacije — koje je primoralo humanoidnu svijest da pretvori sukob u duhovni suverenitet."}
                  </p>

                  <div className="space-y-2 font-mono text-[11px]">
                    <div className="flex items-center gap-2 text-red-300/90 bg-red-950/20 px-2.5 py-1.5 rounded border border-red-500/15">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      <span>{language === "en" ? "War Outcome: Scattered Star Exodus" : "Ishod Rata: Veliki Zvjezdani Egzodus"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 bg-cosmic-black/40 px-2.5 py-1.5 rounded border border-white/5">
                      <span className="w-1.5 h-1.5 rounded-full bg-mystic-purple" />
                      <span>{language === "en" ? "Decryption Stream: Active Archives" : "Dešifriranje: Aktivni Arhivi"}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Action Buttons - Unchanged functionality, styled cleanly */}
            <div className="pt-4 border-t border-red-500/20 flex flex-col md:flex-row md:flex-wrap items-center justify-start gap-3.5 w-full">
              <motion.button
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOpenSubpage(8)}
                className="inline-flex items-center justify-center gap-2.5 py-3 px-6 bg-gradient-to-r from-red-600/80 to-amber-600/80 hover:from-red-600 hover:to-amber-600 text-white font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_15px_rgba(239,68,68,0.2)] hover:shadow-[0_0_25px_rgba(239,68,68,0.45)] border border-red-500/30 hover:border-red-400 transition-all cursor-pointer group w-full md:w-auto"
              >
                <Flame className="w-4 h-4 text-white group-hover:rotate-12 transition-transform" />
                <span>
                  {language === "en" ? "Lyran-Orion War" : "Lyran-Orion War"}
                </span>
                <ArrowUpRight className="w-4 h-4 text-white opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOpenSubpage(5)}
                className="inline-flex items-center justify-center gap-2.5 py-3 px-6 bg-cosmic-black/60 hover:bg-cosmic-black/90 text-quantum-turquoise font-bold uppercase text-[11px] tracking-widest rounded-md border border-quantum-turquoise/30 hover:border-quantum-turquoise transition-all cursor-pointer group w-full md:w-auto"
              >
                <BookOpen className="w-4 h-4 text-quantum-turquoise group-hover:rotate-6 transition-transform" />
                <span>
                  {language === "en" ? "Compare Star Systems" : "Istraži Komparativne Sustave"}
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOpenSubpage(10)}
                className="inline-flex items-center justify-center gap-2.5 py-3 px-6 bg-gradient-to-r from-red-950/90 via-amber-950/80 to-[#1C080E] hover:from-red-600 hover:to-amber-600 text-white font-bold uppercase text-[11px] tracking-widest rounded-md border border-red-500/50 hover:border-red-400 shadow-[0_0_20px_rgba(239,68,68,0.3)] hover:shadow-[0_0_30px_rgba(239,68,68,0.6)] transition-all cursor-pointer group relative overflow-hidden w-full md:w-auto"
              >
                <Flame className="w-4 h-4 text-red-400 group-hover:text-white group-hover:rotate-12 transition-all" />
                <span>
                  {language === "en" ? "The Fall of Lyra" : "Pad Lyre — Slom"}
                </span>
                <span className="absolute top-1 right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-white opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOpenSubpage(9)}
                className="inline-flex items-center justify-center gap-2.5 py-3 px-6 bg-gradient-to-r from-quantum-turquoise/15 to-mystic-purple/15 hover:from-quantum-turquoise/35 hover:to-mystic-purple/35 text-white font-bold uppercase text-[11px] tracking-widest rounded-md border border-quantum-turquoise/45 hover:border-quantum-turquoise transition-all cursor-pointer group relative overflow-hidden w-full md:w-auto shadow-[0_0_15px_rgba(0,212,255,0.15)] hover:shadow-[0_0_25px_rgba(0,212,255,0.35)]"
              >
                <Compass className="w-4 h-4 text-quantum-turquoise group-hover:rotate-12 transition-transform" />
                <span>
                  {language === "en" ? "Exodus" : "Exodus"}
                </span>
                <span className="absolute top-1 right-1 flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-quantum-turquoise opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-quantum-turquoise"></span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-white opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </motion.button>
            </div>
          </div>
        </section>

        {/* SECTION 7: Evolucijski modeli i metafizika */}
        <section id="evolution-section" className="bg-cosmic-indigo/20 backdrop-blur-md border-l-4 border-mystic-purple rounded-r-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-mystic-purple bg-mystic-purple/10 px-3 py-1 rounded-full border border-mystic-purple/20 inline-block">
                  {language === "en" ? "Section 7: Ascensional Metaphysics" : "Sekcija 7: Metafizika Uzašašća"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
                  {t.sec7Title}
                </h2>
                <p className="text-sm md:text-base text-celestial-light leading-relaxed font-sans font-light">
                  {t.sec7Intro}
                </p>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "The architectural mapping of spiritual expansion across physical densities (1D up to 6D light networks). Align your brain-heart electromagnetic frequencies to the 8Hz Schumann wave bridge to entrain into the galactic net."
                    : "Arhitektonsko mapiranje duhovnog širenja kroz dimenzionalne gustoće (od 1D do 6D). Uskladite elektromagnetske frekvencije srca i mozga s Schumannovim mostom od 8Hz."}
                </p>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-mystic-purple mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-mystic-purple uppercase tracking-wider">{language === "en" ? "Dimensional Scale (1D-6D)" : "Dimenzijska Ljestvica (1D-6D)"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Physical separation to coherent light grids" : "Fizička odvojenost do mreža svjetlosti"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-quantum-turquoise uppercase tracking-wider">{language === "en" ? "Heart-Brain Coherence" : "Koherencija Srca i Mozga"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Generating emotional sine-wave resonance" : "Generiranje emocionalne rezonancije"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-golden-resonance mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-golden-resonance uppercase tracking-wider">{language === "en" ? "8Hz Schumann Antenna" : "Antena od 8Hz (Schumann)"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Somatic entrainment into star transmissions" : "Somatsko usklađivanje sa zvijezdama"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-celestial-light mt-1.5 shrink-0" />
                  <div>
                    <h4 className="text-xs font-mono text-celestial-light uppercase tracking-wider">{language === "en" ? "Shadow Transmutation" : "Transmutacija Sjene"}</h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">{language === "en" ? "Empathy-driven psychological integration" : "Psihička integracija vođena empatijom"}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOpenSubpage(6)}
                  className="inline-flex items-center gap-2.5 py-3.5 px-7 bg-gradient-to-r from-mystic-purple to-golden-resonance text-cosmic-black font-bold uppercase text-[11px] tracking-widest rounded-md shadow-[0_0_20px_rgba(147,51,234,0.35)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] border border-transparent hover:border-white/20 transition-all cursor-pointer group"
                >
                  <BookOpen className="w-4 h-4 text-cosmic-black group-hover:rotate-6 transition-transform" />
                  <span>
                    {language === "en" ? "Explore Consciousness Evolution Models" : "Istraži Modele Evolucije Svijesti"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-cosmic-black opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </motion.button>
              </div>
            </div>

            {/* Side visual box */}
            <div className="lg:col-span-5 bg-cosmic-black/80 border border-mystic-purple/20 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-2xl min-h-[220px]">
              <div>
                <span className="text-[10px] font-mono text-mystic-purple uppercase tracking-widest block mb-1">
                  EVOLUTIONARY DIRECTIVE
                </span>
                <h4 className="text-sm font-mono uppercase tracking-wider text-white font-bold mb-3">
                  {language === "en" ? "The Schumann Bridge" : "Schumannov Most"}
                </h4>
                <p className="text-xs text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "When human individuals align heart and brain rhythm to approximately 8Hz, the body acts as an antenna. This aligns human biology with Earth’s cavity wave and opens a telepathic link to high-frequency systems."
                    : "Kada ljudi usklade ritam srca i mozga na približno 8Hz, tijelo djeluje kao antena. To usklađuje ljudsku biologiju s valom Zemlje i otvara telepatsku vezu."}
                </p>
                <div className="mt-4 p-3 bg-cosmic-indigo/30 border border-mystic-purple/20 rounded text-[10px] font-mono text-mystic-purple">
                  TUNING STATUS: READY // 8.00 HZ ACTIVE
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ERRAN STARFIELD MAP SECTION */}
        <section id="erran-map-section" className="relative w-screen left-1/2 right-1/2 -translate-x-1/2 overflow-hidden py-12 bg-gradient-to-b from-cosmic-black via-[#0A0E1A] to-cosmic-black border-y border-quantum-turquoise/20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00d4ff05_1px,transparent_1px),linear-gradient(to_bottom,#00d4ff05_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/20 inline-block">
              {language === "en" ? "Interactive Holographic Navigation Grid" : "Interaktivna holografska navigacijska mreža"}
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight mt-3">
              {language === "en" ? "ERRAN STARFIELD MAP — Taygeta Sector M45" : "ERRAN STARFIELD MAP — Sektor Taygeta M45"}
            </h2>
            <p className="text-sm text-celestial-light leading-relaxed font-sans font-light mt-2 max-w-3xl">
              {language === "en"
                ? "Quantum interstellar telemetry capture displaying the plasmatic resonance grid coordinates of the Taygeta system and Erra's planetary orbit."
                : "Kvantni međuzvjezdani telemetrijski snimak koji prikazuje koordinate plazmatske rezonantne mreže sustava Taygeta i Errine orbite."}
            </p>
          </div>

          {/* Large Image Container */}
          <div className="relative w-full max-w-[100vw] overflow-hidden bg-cosmic-black/60 flex flex-col items-center justify-center">
            {/* Scanning Progress Bar */}
            <div className="absolute top-0 inset-x-0 z-40 bg-cosmic-black/85 backdrop-blur-md border-b border-quantum-turquoise/20 px-4 md:px-8 py-2 md:py-3 flex items-center justify-between gap-4 text-xs font-mono">
              <div className="flex items-center gap-2 text-quantum-turquoise overflow-hidden">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-quantum-turquoise animate-pulse shrink-0"></span>
                <span className="tracking-widest uppercase font-bold text-[9px] md:text-xs truncate">
                  {language === "en" ? "M45 SECTOR SCAN ACTIVE" : "SKENIRANJE SEKTORA M45 AKTIVNO"}
                </span>
                {isGlitching && (
                  <span className="text-red-500 text-[9px] md:text-xs animate-pulse font-bold tracking-widest pl-2 uppercase shrink-0">
                    // TELEMETRY GLITCH
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 md:gap-3 w-1/3 min-w-[120px] max-w-[200px] md:max-w-[300px] shrink-0">
                <div className="relative w-full h-1 md:h-1.5 bg-quantum-turquoise/15 rounded-full overflow-hidden border border-quantum-turquoise/10">
                  <div 
                    className="absolute top-0 left-0 h-full bg-quantum-turquoise shadow-[0_0_8px_rgba(0,212,255,0.8)] transition-all duration-100 ease-linear"
                    style={{ width: `${scanProgress}%` }}
                  />
                </div>
                <span className="text-quantum-turquoise text-[9px] md:text-xs font-bold min-w-[32px] text-right">
                  {scanProgress}%
                </span>
              </div>
            </div>

            {/* HUD Corner markers */}
            <div className="absolute top-16 left-4 z-10 w-6 h-6 border-t-2 border-l-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute top-16 right-4 z-10 w-6 h-6 border-t-2 border-r-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute bottom-4 left-4 z-10 w-6 h-6 border-b-2 border-l-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute bottom-4 right-4 z-10 w-6 h-6 border-b-2 border-r-2 border-quantum-turquoise/40 pointer-events-none"></div>

            {/* Glowing neon borders */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-quantum-turquoise/50 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-quantum-turquoise/50 to-transparent"></div>

            {/* Help/Status overlay */}
            <div className="absolute bottom-6 left-6 z-40 hidden md:flex items-center gap-2 bg-cosmic-black/75 backdrop-blur-sm border border-quantum-turquoise/20 px-3 py-1.5 rounded text-[10px] font-mono text-quantum-turquoise/70 pointer-events-none">
              <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-ping"></span>
              <span>
                {mapScale > 1
                  ? (language === "en" ? "DRAG TO PAN STARGRID" : "POVUCI ZA POMicanje REŠETKE")
                  : (language === "en" ? "PINCH / DRAG / WHEEL TO ZOOM INTERACTIVE GRID" : "ZAKRENI / POVUCI / KOTAČIĆ ZA ZUMIRANJE REŠETKE")
                }
              </span>
            </div>

            {/* Scanning Laser Beam Effect */}
            <div 
              className="relative w-full overflow-hidden flex items-center justify-center select-none pt-12"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              <motion.div
                animate={{
                  top: ["0%", "100%", "0%"]
                }}
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                style={{ position: "absolute" }}
                className={`left-0 right-0 h-[1.5px] bg-quantum-turquoise shadow-[0_0_12px_rgba(0,212,255,0.95)] z-40 pointer-events-none transition-all ${
                  isGlitching 
                    ? "skew-x-12 scale-y-[3.5] translate-x-3 opacity-100 shadow-[0_0_20px_rgba(0,212,255,1)]" 
                    : "opacity-85"
                }`}
              />

              {/* Glitch Offset Lines */}
              {isGlitching && (
                <>
                  <motion.div
                    animate={{
                      top: ["0%", "100%", "0%"]
                    }}
                    transition={{
                      duration: 12,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{ position: "absolute" }}
                    className="left-0 right-0 h-[1px] bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] z-40 pointer-events-none translate-y-[-4px] translate-x-[-12px] opacity-70"
                  />
                  <motion.div
                    animate={{
                      top: ["0%", "100%", "0%"]
                    }}
                    transition={{
                      duration: 12,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{ position: "absolute" }}
                    className="left-0 right-0 h-[1px] bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)] z-40 pointer-events-none translate-y-[4px] translate-x-[12px] opacity-70"
                  />
                </>
              )}

              {/* Invisible interactive protection & gesture control overlay */}
              <div 
                className={`absolute inset-0 z-30 bg-transparent ${
                  mapScale > 1
                    ? isDraggingMap
                      ? "cursor-grabbing"
                      : "cursor-grab"
                    : "cursor-default"
                }`}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                onMouseDown={(e) => {
                  if (mapScale > 1) {
                    setIsDraggingMap(true);
                    setDragStart({
                      x: e.clientX - mapPosition.x,
                      y: e.clientY - mapPosition.y,
                    });
                  }
                }}
                onMouseMove={(e) => {
                  if (isDraggingMap) {
                    const newX = e.clientX - dragStart.x;
                    const newY = e.clientY - dragStart.y;
                    setMapPosition(clampPosition(newX, newY, mapScale));
                  }

                  // Tracking specific coordinates inside the map container
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;

                  const pctX = (x / rect.width) * 100;
                  const pctY = (y / rect.height) * 100;

                  // Taygeta Sector M45 relative coordinates (RA 03h 47m, Dec +24°06')
                  const raHour = 3;
                  const raMin = Math.floor(43 + (pctX / 100) * 8); // 43m to 51m
                  const raSec = Math.floor((pctX * 60) % 60);
                  const decDeg = 23 + (pctY > 50 ? 1 : 0);
                  const decMin = Math.floor(Math.abs((pctY / 100) * 120 - 60));
                  const decSec = Math.floor((pctY * 60) % 60);

                  const raStr = `${raHour.toString().padStart(2, '0')}h ${raMin.toString().padStart(2, '0')}m ${raSec.toString().padStart(2, '0')}s`;
                  const decStr = `+${decDeg}° ${decMin.toString().padStart(2, '0')}' ${decSec.toString().padStart(2, '0')}"`;

                  const sectorX = Math.floor(pctX / 5);
                  const sectorY = Math.floor(pctY / 5);
                  const sectorID = `M45-TYG-[${sectorX.toString().padStart(2, '0')}:${sectorY.toString().padStart(2, '0')}]`;

                  const freq = (432 + (pctX * 0.96)).toFixed(2);

                  setHoverCoords({
                    clientX: e.clientX,
                    clientY: e.clientY,
                    containerX: x,
                    containerY: y,
                    rectWidth: rect.width,
                    rectHeight: rect.height,
                    ra: raStr,
                    dec: decStr,
                    sector: sectorID,
                    resonance: `${freq} Hz`
                  });
                }}
                onMouseUp={() => setIsDraggingMap(false)}
                onMouseLeave={() => {
                  setIsDraggingMap(false);
                  setHoverCoords(null);
                }}
                onTouchStart={(e) => {
                  if (e.touches.length === 1) {
                    setIsDraggingMap(true);
                    setDragStart({
                      x: e.touches[0].clientX - mapPosition.x,
                      y: e.touches[0].clientY - mapPosition.y,
                    });
                    setLastTouchDistance(null);
                  } else if (e.touches.length === 2) {
                    setIsDraggingMap(false);
                    const dist = Math.hypot(
                      e.touches[0].clientX - e.touches[1].clientX,
                      e.touches[0].clientY - e.touches[1].clientY
                    );
                    setLastTouchDistance(dist);
                  }
                }}
                onTouchMove={(e) => {
                  if (e.touches.length === 1 && isDraggingMap) {
                    const newX = e.touches[0].clientX - dragStart.x;
                    const newY = e.touches[0].clientY - dragStart.y;
                    setMapPosition(clampPosition(newX, newY, mapScale));
                  } else if (e.touches.length === 2 && lastTouchDistance !== null) {
                    const dist = Math.hypot(
                      e.touches[0].clientX - e.touches[1].clientX,
                      e.touches[0].clientY - e.touches[1].clientY
                    );
                    const delta = dist - lastTouchDistance;
                    const scaleFactor = delta * 0.007;
                    const newScale = Math.max(1, Math.min(4, mapScale + scaleFactor));
                    setMapScale(newScale);
                    setLastTouchDistance(dist);
                    setMapPosition(prev => clampPosition(prev.x, prev.y, newScale));
                  }
                }}
                onTouchEnd={() => {
                  setIsDraggingMap(false);
                  setLastTouchDistance(null);
                }}
                onWheel={(e) => {
                  e.preventDefault();
                  const zoomIntensity = 0.08;
                  const delta = e.deltaY < 0 ? zoomIntensity : -zoomIntensity;
                  const newScale = Math.max(1, Math.min(4, mapScale + delta));
                  setMapScale(newScale);
                  setMapPosition(prev => clampPosition(prev.x, prev.y, newScale));
                }}
              />

              {/* Scanlines overlay effect */}
              <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,212,255,0.04)_1px,transparent_1px)] bg-[size:100%_5px] pointer-events-none z-10 opacity-70"></div>
              {/* Vignette & tint overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black via-transparent to-cosmic-black opacity-40 z-10 pointer-events-none"></div>
              
              <img
                src={erranStarfieldMapImg}
                alt="ERRAN STARFIELD MAP Holographic Navigation Grid — Taygeta Sector"
                width="1600"
                height="1200"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  transform: `translate(${mapPosition.x}px, ${mapPosition.y}px) scale(${mapScale})`,
                  transition: isDraggingMap ? "none" : "transform 0.15s ease-out",
                  transformOrigin: "center center"
                }}
                className={`w-full h-auto block object-contain max-h-[85vh] select-none pointer-events-none transition-all duration-75 ${
                  isGlitching 
                    ? "opacity-75 blur-[0.4px] contrast-125 saturate-150 scale-[1.005] translate-x-[2px]" 
                    : "opacity-95"
                }`}
              />

              {/* Floating Astrometric Tooltip */}
              {hoverCoords && (
                <div 
                  className="absolute pointer-events-none z-50 bg-cosmic-black/90 backdrop-blur-md border border-quantum-turquoise/40 p-3 rounded-lg shadow-[0_0_15px_rgba(0,212,255,0.25)] text-[10px] font-mono text-quantum-turquoise w-48 transition-all duration-75"
                  style={{
                    left: `${
                      hoverCoords.containerX + 192 + 30 > hoverCoords.rectWidth 
                        ? hoverCoords.containerX - 192 - 15 
                        : hoverCoords.containerX + 15
                    }px`,
                    top: `${
                      hoverCoords.containerY + 110 + 30 > hoverCoords.rectHeight 
                        ? hoverCoords.containerY - 110 - 15 
                        : hoverCoords.containerY + 15
                    }px`,
                  }}
                >
                  <div className="flex items-center justify-between border-b border-quantum-turquoise/20 pb-1.5 mb-1.5 font-bold">
                    <span className="uppercase tracking-widest text-[9px]">
                      {language === "en" ? "ASTROMETRIC TELEMETRY" : "ASTROMETRIJSKA TELEMETRIJA"}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full ${isGlitching ? 'bg-red-500 animate-ping' : 'bg-quantum-turquoise animate-pulse'}`} />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-celestial-light/60">{language === "en" ? "SECTOR:" : "SEKTOR:"}</span>
                      <span className="text-white font-bold">{hoverCoords.sector}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-celestial-light/60">{language === "en" ? "R.A.:" : "R.A.:"}</span>
                      <span className="text-white">{hoverCoords.ra}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-celestial-light/60">{language === "en" ? "DEC:" : "DEK.:"}</span>
                      <span className="text-white">{hoverCoords.dec}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-celestial-light/60">{language === "en" ? "RESONANCE:" : "REZONANCIJA:"}</span>
                      <span className="text-quantum-turquoise font-bold">{hoverCoords.resonance}</span>
                    </div>
                    <div className="flex justify-between border-t border-quantum-turquoise/10 pt-1.5 mt-1.5 text-[9px]">
                      <span className="text-celestial-light/50">{language === "en" ? "SIGNAL STATUS:" : "STATUS SIGNALA:"}</span>
                      <span className={isGlitching ? "text-red-400 animate-pulse font-bold" : "text-emerald-400 font-bold"}>
                        {isGlitching 
                          ? (language === "en" ? "GLITCH_DETECTED" : "DETEKTIRANA_SMETNJA") 
                          : (language === "en" ? "STABLE_LOCK" : "STABILNA_VEZA")
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA & Holographic Data Readout */}
          <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 flex flex-col items-center gap-6">
            <div className="w-full flex flex-col md:flex-row items-center justify-center gap-4">
              {/* Decode button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsErranHoloOpen(!isErranHoloOpen)}
                className="inline-flex items-center gap-2.5 py-4 px-8 bg-quantum-turquoise hover:bg-quantum-turquoise/80 text-cosmic-black font-mono font-bold uppercase text-[11px] tracking-[0.2em] rounded-md shadow-[0_0_20px_rgba(0,212,255,0.45)] transition-all cursor-pointer border border-quantum-turquoise/35 h-[52px]"
                id="erran-holo-cta-btn"
              >
                <Radio className={`w-4 h-4 text-cosmic-black ${isErranHoloOpen ? "animate-pulse" : ""}`} />
                <span>
                  {isErranHoloOpen
                    ? (language === "en" ? "CLOSE HOLOGRAPHIC STREAM" : "ZATVORI HOLOGRAFSKI PROTOK")
                    : (language === "en" ? "DECODE HOLOGRAPHIC TELEMETRY" : "DEKODIRAJ HOLOGRAFSKU TELEMETRIJU")
                  }
                </span>
              </motion.button>

              {/* Magnification HUD Controls */}
              <div className="flex items-center gap-3 bg-cosmic-black/85 backdrop-blur-md border border-quantum-turquoise/35 p-2 px-3 rounded-lg shadow-[0_0_15px_rgba(0,212,255,0.25)] select-none h-[52px]">
                <div className="flex flex-col items-start justify-center gap-0.5 pr-3 border-r border-quantum-turquoise/20 h-full">
                  <span className="text-[9px] font-mono tracking-widest text-quantum-turquoise/70 uppercase leading-none">Telemetry Zoom</span>
                  <span className="text-xs font-mono font-bold text-quantum-turquoise leading-none mt-0.5">
                    {(mapScale * 100).toFixed(0)}% <span className="text-[10px] text-quantum-turquoise/50">({mapScale === 1 ? "1.0x" : `${mapScale.toFixed(1)}x`})</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleZoomOut}
                    disabled={mapScale <= 1}
                    title="Zoom Out"
                    aria-label="Zoom Out"
                    className={`w-8 h-8 rounded border flex items-center justify-center text-sm font-bold font-mono transition-all ${
                      mapScale <= 1
                        ? "border-quantum-turquoise/15 text-quantum-turquoise/20 cursor-not-allowed"
                        : "border-quantum-turquoise/40 text-quantum-turquoise hover:bg-quantum-turquoise/10 active:scale-95 cursor-pointer"
                    }`}
                  >
                    -
                  </button>
                  <button
                    onClick={handleZoomIn}
                    disabled={mapScale >= 4}
                    title="Zoom In"
                    aria-label="Zoom In"
                    className={`w-8 h-8 rounded border flex items-center justify-center text-sm font-bold font-mono transition-all ${
                      mapScale >= 4
                        ? "border-quantum-turquoise/15 text-quantum-turquoise/20 cursor-not-allowed"
                        : "border-quantum-turquoise/40 text-quantum-turquoise hover:bg-quantum-turquoise/10 active:scale-95 cursor-pointer"
                    }`}
                  >
                    +
                  </button>
                  <button
                    onClick={handleResetZoom}
                    disabled={mapScale === 1 && mapPosition.x === 0 && mapPosition.y === 0}
                    title="Reset Map"
                    className={`px-2.5 h-8 rounded border flex items-center gap-1 text-[10px] font-mono tracking-wider uppercase font-medium transition-all ${
                      mapScale === 1 && mapPosition.x === 0 && mapPosition.y === 0
                        ? "border-quantum-turquoise/15 text-quantum-turquoise/20 cursor-not-allowed"
                        : "border-quantum-turquoise/40 text-quantum-turquoise hover:bg-quantum-turquoise/10 active:scale-95 cursor-pointer"
                    }`}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Collapsible Decoded Data Panel */}
            <AnimatePresence>
              {isErranHoloOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="w-full overflow-hidden"
                >
                  <div className="bg-[#0D1526]/85 border border-quantum-turquoise/35 rounded-xl p-6 md:p-8 font-mono text-xs text-celestial-light relative shadow-[0_0_25px_rgba(0,212,255,0.15)] mt-4">
                    {/* Corner aesthetics */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-quantum-turquoise/40 pointer-events-none"></div>
                    <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-quantum-turquoise/40 pointer-events-none"></div>
                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-quantum-turquoise/40 pointer-events-none"></div>
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-quantum-turquoise/40 pointer-events-none"></div>

                    {/* Neon stream indicators */}
                    <div className="flex items-center justify-between mb-6 border-b border-quantum-turquoise/20 pb-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-quantum-turquoise animate-ping"></span>
                        <span className="text-sm font-bold tracking-wider text-white">
                          {language === "en" ? "TAYGETA SEC-04 QUANTUM OVERLAY" : "TAYGETA SEK-04 KVANATNI PREKLOP"}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-500">FEED STATUS: STREAMING_ACTIVE</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                      {/* Grid Coordinates */}
                      <div className="space-y-3 bg-cosmic-black/40 p-4 rounded border border-quantum-turquoise/15">
                        <h4 className="text-[11px] text-white font-bold tracking-widest uppercase border-b border-quantum-turquoise/10 pb-1.5 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise"></span>
                          {language === "en" ? "Grid Calibration" : "Kalibracija Mreže"}
                        </h4>
                        <ul className="space-y-1.5 text-[11px] text-gray-300">
                          <li>• <strong className="text-quantum-turquoise">Sector:</strong> M45-Taygeta-Grid</li>
                          <li>• <strong className="text-quantum-turquoise">Sub-Sector:</strong> Erran Core IV</li>
                          <li>• <strong className="text-quantum-turquoise">Relative RA:</strong> 03h 45m 12.5s</li>
                          <li>• <strong className="text-quantum-turquoise">Relative Dec:</strong> +24° 28' 02"</li>
                          <li>• <strong className="text-quantum-turquoise">Lidar Scan Range:</strong> 440.2 LY</li>
                        </ul>
                      </div>

                      {/* Planetary Data */}
                      <div className="space-y-3 bg-cosmic-black/40 p-4 rounded border border-quantum-turquoise/15">
                        <h4 className="text-[11px] text-white font-bold tracking-widest uppercase border-b border-quantum-turquoise/10 pb-1.5 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise"></span>
                          {language === "en" ? "Erra System Bio-Metrics" : "Bio-metrika Sustava Erra"}
                        </h4>
                        <ul className="space-y-1.5 text-[11px] text-gray-300">
                          <li>• <strong className="text-quantum-turquoise">Core Body:</strong> Erra (5th Density)</li>
                          <li>• <strong className="text-quantum-turquoise">Atmosphere:</strong> Plasmatic Oxygenized</li>
                          <li>• <strong className="text-quantum-turquoise">Gravity Ratio:</strong> 1.03x Earth G</li>
                          <li>• <strong className="text-quantum-turquoise">Population index:</strong> 420M (Telepathic)</li>
                          <li>• <strong className="text-quantum-turquoise">Council Chamber:</strong> Crystal spires</li>
                        </ul>
                      </div>

                      {/* Vector Channels */}
                      <div className="space-y-3 bg-cosmic-black/40 p-4 rounded border border-quantum-turquoise/15">
                        <h4 className="text-[11px] text-white font-bold tracking-widest uppercase border-b border-quantum-turquoise/10 pb-1.5 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise"></span>
                          {language === "en" ? "Interstellar Vectors" : "Međuzvjezdani Vektori"}
                        </h4>
                        <ul className="space-y-1.5 text-[11px] text-gray-300">
                          <li>• <strong className="text-quantum-turquoise">Vector A:</strong> Maia Corridor (Active)</li>
                          <li>• <strong className="text-quantum-turquoise">Vector B:</strong> Alcyone Core (Stable)</li>
                          <li>• <strong className="text-quantum-turquoise">Vector C:</strong> Earth-Sol Core Bridge</li>
                          <li>• <strong className="text-quantum-turquoise">Base Carrier:</strong> 234.80 GHz</li>
                          <li>• <strong className="text-quantum-turquoise">Telemetry Sync:</strong> Schumann-8Hz</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-quantum-turquoise/20 text-center text-[10px] text-gray-500 leading-relaxed font-sans">
                      {language === "en"
                        ? "NOTICE: This telemetry is an educational representation of the sacred galactic geography. Real-time updates depend on cosmic telemetry-link and local Schumann-antenna alignment."
                        : "NAPOMENA: Ova telemetrija je edukativni prikaz svete galaktičke geografije. Ažuriranja u realnom vremenu ovise o kozmičkoj telemetrijskoj vezi i Schumann-antenskom poravnanju."}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* SECTION 8 & INTERACTIVE PORTAL: Star Map & Frequency Synth */}
        <section id="interactive-portal" className="space-y-12">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/20">
              {language === "en" ? "Section 8: Interactive Portal" : "Sekcija 8: Interaktivni Portal"}
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight">
              {t.sec8Title}
            </h2>
            <p className="text-sm text-celestial-light leading-relaxed font-sans font-light">
              {t.sec8Intro}
            </p>
          </div>

          {/* Star Map module */}
          <div className="space-y-4">
            <div className="border-b border-pleiadian-blue/20 pb-3 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-mono uppercase tracking-wider text-white font-semibold">
                {t.interactiveMapTitle}
              </h4>
              <p className="text-[11px] text-celestial-light font-sans font-light max-w-xl">
                {t.interactiveMapDesc}
              </p>
            </div>
            <Suspense fallback={
              <div className="w-full h-96 flex flex-col items-center justify-center space-y-3 font-mono text-quantum-turquoise bg-cosmic-black/60 rounded-xl border border-quantum-turquoise/20">
                <div className="w-8 h-8 border-2 border-quantum-turquoise border-t-transparent rounded-full animate-spin" />
                <span className="text-xs uppercase tracking-widest animate-pulse">Loading Celestial Navigation Grid...</span>
              </div>
            }>
              <StarMap
                language={language}
                onTuneIn={handleTuneIn}
                activeSynthFrequency={activeSynthFreq}
              />
            </Suspense>
          </div>

          {/* Frequency Synth module - Collapsible Holographic Receiver */}
          <div id="synth-section" className="border-t border-pleiadian-blue/20 bg-cosmic-black/40">
            {/* CTA Trigger Bar */}
            <div className="p-4 md:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-cosmic-black via-cosmic-black/90 to-cosmic-black/85">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${isSynthOpen ? "bg-quantum-turquoise animate-ping" : "bg-gray-600"}`}></div>
                <div className="text-left">
                  <span className="text-[9px] text-pleiadian-blue uppercase tracking-widest block font-bold font-mono">RESONANT AUDIO SCHEMATICS</span>
                  <h4 className="text-xs md:text-sm text-white font-bold tracking-widest uppercase font-mono">
                    {t.frequencySynthTitle}
                  </h4>
                </div>
              </div>

              <button
                onClick={() => {
                  if (!isSynthOpen) {
                    setActiveSynthFreq(432); // Tune Alcyone (432Hz)
                  }
                  setIsSynthOpen(!isSynthOpen);
                }}
                className={`w-full sm:w-auto px-5 py-2 font-mono text-[10px] md:text-xs tracking-wider uppercase border rounded-md cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 shadow-[0_0_12px_rgba(0,212,255,0.05)] ${
                  isSynthOpen
                    ? "bg-quantum-turquoise/20 text-quantum-turquoise border-quantum-turquoise hover:bg-quantum-turquoise/30"
                    : "bg-cosmic-black text-celestial-light border-quantum-turquoise/40 hover:border-quantum-turquoise hover:text-quantum-turquoise hover:bg-quantum-turquoise/10"
                }`}
              >
                <Radio className={`w-3.5 h-3.5 ${isSynthOpen ? "animate-pulse text-quantum-turquoise" : ""}`} />
                {isSynthOpen
                  ? (language === "en" ? "DISCONNECT FREQUENCY SYNTH" : "ODSPOJI SINTESAJZER")
                  : "TUNE SYNTH TO ALACYONE"
                }
              </button>
            </div>

            {/* Collapsible Holographic Synthesizer Area */}
            <AnimatePresence>
              {isSynthOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="p-6 md:p-8 font-mono text-xs text-gray-300 bg-quantum-turquoise/[0.01] border-t border-b border-quantum-turquoise/25 relative">
                    
                    {/* Holographic grid overlay */}
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,212,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,212,255,0.02)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none opacity-60"></div>
                    
                    {/* Interactive Scanline Overlay */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(0,212,255,0.04)_50%)] bg-[size:100%_6px] pointer-events-none opacity-80"></div>
                    
                    {/* Cybernetic Corner Brackets */}
                    <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-quantum-turquoise/50 pointer-events-none"></div>
                    <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-quantum-turquoise/50 pointer-events-none"></div>
                    <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-quantum-turquoise/50 pointer-events-none"></div>
                    <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-quantum-turquoise/50 pointer-events-none"></div>

                    {/* Floating holographic pulse light */}
                    <div className="absolute -top-10 left-1/3 w-96 h-24 bg-quantum-turquoise/5 rounded-full blur-[60px] pointer-events-none animate-pulse"></div>

                    {/* Description Inside Holographic Panel */}
                    <p className="text-[11px] text-celestial-light font-sans font-light max-w-xl mb-4 relative z-10 leading-relaxed">
                      {t.frequencySynthDesc}
                    </p>

                    <div className="relative z-10">
                      <Suspense fallback={
                        <div className="w-full p-8 flex flex-col items-center justify-center space-y-3 font-mono text-quantum-turquoise">
                          <div className="w-6 h-6 border-2 border-quantum-turquoise border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs uppercase tracking-widest animate-pulse">Initializing Audio Synthesizer...</span>
                        </div>
                      }>
                        <FrequencySynth
                          language={language}
                          externalFrequency={activeSynthFreq}
                        />
                      </Suspense>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* SECTION 9: PLEIADES GALACTIC LIBRARY GATEWAY */}
        <section id="library-section" className="space-y-8">
          {/* Architectural Gateway Card - Majestic, Clean & Atmospheric */}
          <div className="relative rounded-2xl bg-gradient-to-br from-[#060a14]/95 via-[#0c1427]/95 to-[#090e1c]/95 border border-quantum-turquoise/30 p-6 sm:p-8 md:p-10 backdrop-blur-xl shadow-[0_0_50px_rgba(0,212,255,0.08)] overflow-hidden">
            {/* Tech HUD corner accents */}
            <div className="absolute top-2.5 left-2.5 w-4 h-4 border-t-2 border-l-2 border-quantum-turquoise/40 pointer-events-none" />
            <div className="absolute top-2.5 right-2.5 w-4 h-4 border-t-2 border-r-2 border-quantum-turquoise/40 pointer-events-none" />
            <div className="absolute bottom-2.5 left-2.5 w-4 h-4 border-b-2 border-l-2 border-quantum-turquoise/40 pointer-events-none" />
            <div className="absolute bottom-2.5 right-2.5 w-4 h-4 border-b-2 border-r-2 border-quantum-turquoise/40 pointer-events-none" />

            {/* Glowing background nebula aura */}
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-quantum-turquoise/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-mystic-purple/10 rounded-full blur-3xl pointer-events-none" />

            <div className="space-y-6 sm:space-y-7 relative z-10">
              {/* Top Row: Two Columns (Left: Title, Subtitle, Quote, Node // Right: Archive Preview & Stats) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                
                {/* Left Column: Header, Badges, Quote, Horizontal Archive Node */}
                <div className="lg:col-span-7 space-y-6">
                  
                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-space-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/25 inline-flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-quantum-turquoise" />
                      {language === "en" ? "SECTION 9 // ARCHIVAL SANCTUARY" : "SEKCIJA 9 // ARHIVSKO SVETIŠTE"}
                    </span>
                    <span className="text-xs font-space-mono uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/25 inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      {language === "en" ? "M45 ARCHIVE // 268,000 BCE" : "M45 ARHIV // 268.000 PR. KR."}
                    </span>
                  </div>

                  {/* 1. NASLOV SEKCIJE & PODNASLOV U SPACE MONO */}
                  <div className="space-y-2">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-ethereal-white tracking-wide flex items-center gap-3">
                      <BookMarked className="w-7 h-7 md:w-8 md:h-8 text-quantum-turquoise drop-shadow-[0_0_12px_rgba(0,212,255,0.6)] shrink-0" />
                      <span>
                        {language === "en" ? "PLEIADIAN GALACTIC LIBRARY" : "PLEJADANSKA GALAKTIČKA KNJIŽNICA"}
                      </span>
                    </h2>
                    <p className="font-space-mono text-xs md:text-sm text-quantum-turquoise/90 tracking-widest uppercase font-semibold">
                      {language === "en"
                        ? "FRAGMENTS OF THE LYRAN ARCHIVE — PARTIALLY DECRYPTED"
                        : "FRAGMENTI LYRANSKOG ARHIVA — DJELOMIČNO DEŠIFRIRANI"}
                    </p>
                  </div>

                  {/* 2. OPIS TEKSTA: Cormorant Garamond, italic */}
                  <div className="pl-4 border-l-2 border-quantum-turquoise/50 py-0.5">
                    <p className="font-cormorant italic text-[1.15rem] md:text-[1.25rem] text-ethereal-white/95 leading-relaxed tracking-wide">
                      {language === "en"
                        ? "These texts were not written. They were translated. From an archive that has existed 268,000 years before anyone knew to search for it."
                        : "Ovi tekstovi nisu napisani. Oni su prevedeni. Iz arhiva koji postoji 268,000 godina prije nego je itko znao da ga traži."}
                    </p>
                  </div>

                  {/* HORIZONTALNI ARCHIVE NODE */}
                  <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 text-[11px] font-space-mono text-quantum-turquoise/90 bg-cosmic-black/70 border border-quantum-turquoise/30 rounded-lg px-3.5 py-2 w-fit shadow-[0_0_15px_rgba(0,212,255,0.06)]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-quantum-turquoise animate-pulse" />
                      <span className="font-bold tracking-wider text-quantum-turquoise">ARCHIVE NODE: ATLAN-1</span>
                    </div>
                    <span className="text-quantum-turquoise/40">//</span>
                    <span className="text-gray-300">
                      {language === "en" ? "DEPTH: 800M" : "DUBINA: 800M"}
                    </span>
                    <span className="text-quantum-turquoise/40">//</span>
                    <span className="text-emerald-400 font-bold flex items-center gap-1.5">
                      <Radio className="w-3 h-3 text-emerald-400 animate-pulse" />
                      STATUS: TRANSMITTING
                    </span>
                  </div>
                </div>

                {/* Right Column: 4. ARCHIVE PREVIEW KARTICA + 7. STATISTIKA */}
                <div className="lg:col-span-5 space-y-3">
                  <div 
                    onClick={() => {
                      if (typeof window !== "undefined") {
                        window.history.pushState({}, "", "/pleiades-library");
                      }
                      setIsLibrarySubpageOpen(true);
                    }}
                    className="bg-[#060a14]/90 border border-quantum-turquoise/25 hover:border-quantum-turquoise/70 rounded-xl p-5 md:p-6 backdrop-blur-md transition-all duration-300 group cursor-pointer hover:-translate-y-1 shadow-2xl hover:shadow-[0_0_30px_rgba(0,212,255,0.18)] relative overflow-hidden space-y-4"
                  >
                    {/* Subtle top ambient line */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-quantum-turquoise/50 to-transparent" />

                    {/* Header: Label & Status Badge */}
                    <div className="flex items-center justify-between text-[11px] font-space-mono">
                      <span className="text-quantum-turquoise font-bold flex items-center gap-1.5 tracking-wider uppercase">
                        <Star className="w-3.5 h-3.5 text-quantum-turquoise fill-quantum-turquoise/30" />
                        {language === "en" ? "ARCHIVE PREVIEW" : "PREGLED ARHIVE"}
                      </span>
                      <span className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-space-mono text-[10px] tracking-widest px-2.5 py-0.5 rounded-full font-bold uppercase inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {language === "en" ? "AVAILABLE" : "DOSTUPNO"}
                      </span>
                    </div>

                    {/* Naslov */}
                    <h4 className="text-xl font-serif font-bold text-[#e8dcc8] group-hover:text-white transition-colors">
                      {language === "en" ? "Book of the First Separation" : "Knjiga Prvog Razdvajanja"}
                    </h4>

                    {/* Opis */}
                    <blockquote className="font-cormorant italic text-sm md:text-base text-[#e8dcc8]/90 leading-relaxed pl-3.5 border-l-2 border-quantum-turquoise/50">
                      "{language === "en"
                        ? "In the beginning there was neither darkness nor light, for there was no eye to distinguish one from the other."
                        : "U početku nije bila tama ni svjetlost jer nije bilo oka koje bi razlikovalo jedno od drugog."}"
                    </blockquote>

                    {/* Tagovi */}
                    <div className="flex flex-wrap gap-1.5 text-[10px] font-space-mono text-gray-300 pt-1">
                      <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-quantum-turquoise font-medium">
                        Fragment_01
                      </span>
                      <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-gray-300">
                        {language === "en" ? "First Separation" : "Prvo Razdvajanje"}
                      </span>
                      <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-gray-300">
                        {language === "en" ? "268,000 BCE" : "268.000 pr. Kr."}
                      </span>
                      <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-300">
                        {language === "en" ? "Available" : "Dostupno"}
                      </span>
                    </div>

                    {/* Bottom Action Hint */}
                    <div className="pt-3 border-t border-white/10 flex items-center justify-between text-xs font-space-mono font-bold text-quantum-turquoise group-hover:text-white transition-colors">
                      <span>
                        {language === "en" ? "Open in library reader" : "Otvori u čitaču knjižnice"}
                      </span>
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </div>

                  {/* 7. STATISTIKA: Ispod preview kartice u Space Mono, sitno */}
                  <div className="pt-2 px-1 text-[10px] sm:text-[11px] font-space-mono text-gray-400 tracking-wider text-center sm:text-left flex flex-wrap items-center justify-between gap-2 border-t border-white/5">
                    <span className="text-quantum-turquoise/90">
                      {language === "en"
                        ? "FRAGMENTS: 10 // DOCUMENTS: 9 // GENESIS: 7 // ARCHIVE: ACTIVE"
                        : "FRAGMENTI: 10 // DOKUMENTI: 9 // GENESIS: 7 // ARHIV: AKTIVAN"}
                    </span>
                    <span className="text-gray-500 text-[10px]">
                      {language === "en" ? "SYSTEM: 100% ONLINE" : "SUSTAV: 100% AKTIVAN"}
                    </span>
                  </div>
                </div>

              </div>

              {/* Middle Row: 4 INFO BOXES IN SINGLE HORIZONTAL ROW — SVE 4 KARTICE ISTE VELIČINE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                {/* BOX 1 — cyan accent (Fragment Books) */}
                <div 
                  onClick={() => handleOpenSubpage(13)}
                  className="p-3.5 rounded-xl bg-cosmic-black/70 border border-quantum-turquoise/30 hover:border-quantum-turquoise/70 shadow-[0_0_18px_rgba(0,212,255,0.08)] hover:shadow-[0_0_24px_rgba(0,212,255,0.2)] transition-all space-y-1.5 group cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  title={language === "en" ? "Open Books of Fragments (10 Sacred Codices)" : "Otvori Knjige Fragmenata (10 svetih kodeksa)"}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-space-mono text-quantum-turquoise uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-quantum-turquoise" />
                      {language === "en" ? "FRAGMENT BOOKS" : "KNJIGE FRAGMENATA"}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-pulse" />
                  </div>
                  <div className="text-xl md:text-2xl font-space-mono font-bold text-white tracking-wider flex items-center gap-2">
                    <span>10 / 10</span>
                    <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-cyan-950/80 text-cyan-300 border border-cyan-500/40">
                      528 Hz
                    </span>
                  </div>
                  <p className="text-[11px] font-space-mono text-gray-400 font-light leading-snug">
                    {language === "en" ? "Translations of crystal archives" : "Prijevodi kristalnih arhiva"}
                  </p>
                </div>

                {/* BOX 2 — crveni/narančasti accent (Omega Black Documents) */}
                <div 
                  onClick={() => handleOpenSubpage(11)}
                  className="p-3.5 rounded-xl bg-cosmic-black/70 border border-[#ff5533]/35 hover:border-[#ff5533]/70 shadow-[0_0_18px_rgba(255,85,51,0.08)] hover:shadow-[0_0_24px_rgba(255,85,51,0.2)] transition-all space-y-1.5 group cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  title={language === "en" ? "Open Omega Black Documents (9 Dossiers)" : "Otvori Omega Black Dokumente (9 dosjea)"}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-space-mono text-[#ff5533] uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5 text-[#ff5533]" />
                      {language === "en" ? "OMEGA BLACK DOCUMENTS" : "OMEGA BLACK DOKUMENTI"}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ff5533]" />
                  </div>
                  <div className="text-xl md:text-2xl font-space-mono font-bold text-white tracking-wider flex items-center gap-2">
                    <span>9 / 9</span>
                    <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-red-950/80 text-red-300 border border-red-500/40">
                      LVL 4
                    </span>
                  </div>
                  <p className="text-[11px] font-space-mono text-gray-400 font-light leading-snug">
                    {language === "en" ? "Level 4 — Partially decrypted" : "Level 4 — Djelomično dešifrirano"}
                  </p>
                </div>

                {/* BOX 3 — zlatni accent (Archive Active) */}
                <div 
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.history.pushState({}, "", "/pleiades-library");
                    }
                    setIsLibrarySubpageOpen(true);
                  }}
                  className="p-3.5 rounded-xl bg-cosmic-black/70 border border-[#f0a500]/35 hover:border-[#f0a500]/70 shadow-[0_0_18px_rgba(240,165,0,0.08)] hover:shadow-[0_0_24px_rgba(240,165,0,0.2)] transition-all space-y-1.5 group cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  title={language === "en" ? "Open Pleiadian Galactic Library Portal" : "Otvori portal Plejadanske Knjižnice"}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-space-mono text-[#f0a500] uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <Radio className="w-3.5 h-3.5 text-[#f0a500]" />
                      {language === "en" ? "ARCHIVE ACTIVE" : "ARHIV AKTIVAN"}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#f0a500] animate-pulse" />
                  </div>
                  <div className="text-xl md:text-2xl font-space-mono font-bold text-[#f0a500] tracking-wider leading-tight">
                    {language === "en" ? "268,000 Y" : "268.000 G"}
                  </div>
                  <p className="text-[11px] font-space-mono text-gray-400 font-light leading-snug">
                    {language === "en" ? "Continuous transmission" : "Kontinuirana transmisija"}
                  </p>
                </div>

                {/* BOX 4 — GENESIS ROSTER (KARTICA U PRAZNOM NASTAVKU, ISTE VELIČINE) */}
                <div 
                  onClick={() => handleOpenSubpage(12)}
                  className="p-3.5 rounded-xl bg-cosmic-black/70 border border-red-500/40 hover:border-red-400 shadow-[0_0_18px_rgba(239,68,68,0.12)] hover:shadow-[0_0_24px_rgba(239,68,68,0.3)] transition-all space-y-1.5 group cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  title={language === "en" ? "Open Genesis Roster (ARK-7 Manifest & 7 Fragments)" : "Otvori Genesis Roster (ARK-7 Manifest i 7 fragmenata)"}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-space-mono text-red-400 uppercase tracking-wider font-bold flex items-center gap-1.5">
                      <Compass className="w-3.5 h-3.5 text-red-400" />
                      GENESIS ROSTER
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" />
                  </div>
                  <div className="text-xl md:text-2xl font-space-mono font-bold text-white tracking-wider flex items-center gap-2">
                    <span>7 / 7</span>
                    <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-red-950/80 text-red-300 border border-red-500/40">
                      431.997 Hz
                    </span>
                  </div>
                  <p className="text-[11px] font-space-mono text-gray-400 font-light leading-snug">
                    {language === "en" ? "ARK-7 manifest // 79,999 souls" : "Manifest ARK-7 // 79.999 duša"}
                  </p>
                </div>

              </div>

              {/* IZVANREDNA SEKCIJA: CTA GUMB ZA PODSTRANICU KRONIKE LYRE (CIJELOM DUŽINOM ISPOD 4 GUMBA) */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
                    }
                    setIsChroniclesOpen(true);
                  }}
                  className="w-full relative overflow-hidden rounded-xl border border-[#FFD27F]/40 hover:border-[#FFD27F] bg-gradient-to-r from-[#0a0d1a] via-[#121428] to-[#080a18] p-4 sm:p-5 shadow-[0_0_24px_rgba(255,210,127,0.15)] hover:shadow-[0_0_36px_rgba(255,210,127,0.32)] transition-all group text-left cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                  title={language === "en" ? "Open Lyra Chronicles • Reconstruction of a Forgotten Civilization" : "Otvori Kronike Lyre • Rekonstrukcija Zaboravljene Civilizacije"}
                >
                  {/* Subtle archaeological relic backdrop & star dust */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,210,127,0.12),transparent_70%)] pointer-events-none" />
                  <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[#FFD27F]/10 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Ambient corner accents */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-[#FFD27F]/40 pointer-events-none" />
                  <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-[#FFD27F]/40 pointer-events-none" />
                  <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-[#FFD27F]/40 pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-[#FFD27F]/40 pointer-events-none" />

                  <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start sm:items-center gap-4">
                      {/* Ancient Geometric Relic Symbol */}
                      <div className="p-2.5 sm:p-3 rounded-lg bg-[#FFD27F]/10 border border-[#FFD27F]/35 text-[#FFD27F] group-hover:border-[#FFD27F] group-hover:bg-[#FFD27F]/20 transition-all shrink-0">
                        <svg className="w-8 h-8 sm:w-9 sm:h-9" viewBox="0 0 100 100" fill="none" stroke="currentColor">
                          <circle cx="50" cy="50" r="42" strokeWidth="1.6" strokeDasharray="4 3" opacity="0.5" />
                          <circle cx="50" cy="50" r="37" strokeWidth="1.8" />
                          {/* Two complete lines */}
                          <line x1="28" y1="42" x2="72" y2="42" strokeWidth="2.2" strokeLinecap="round" />
                          <line x1="28" y1="52" x2="72" y2="52" strokeWidth="2.2" strokeLinecap="round" />
                          {/* Incomplete third line */}
                          <line x1="28" y1="62" x2="52" y2="62" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      </div>

                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] font-space-mono text-[#FFD27F] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#1c180e]/90 border border-[#FFD27F]/40 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3 text-[#FFD27F] animate-pulse" />
                            {language === "en" ? "EXTRAORDINARY ARCHIVAL SECTOR" : "IZVANREDNA ARHIVSKA SEKCIJA"}
                          </span>
                          <span className="text-[10px] font-space-mono text-gray-400">
                            {language === "en" ? "PRIMARY RECONSTRUCTION" : "PRIMARNA REKONSTRUKCIJA"}
                          </span>
                        </div>

                        <h3 className="text-base sm:text-lg md:text-xl font-cormorant font-bold text-white tracking-wide uppercase group-hover:text-[#FFD27F] transition-colors">
                          {language === "en" ? "LYRA CHRONICLES — RECONSTRUCTION OF A FORGOTTEN CIVILIZATION" : "KRONIKE LYRE — REKONSTRUKCIJA ZABORAVLJENE CIVILIZACIJE"}
                        </h3>

                        <p className="text-xs font-sans text-gray-300 font-light leading-relaxed max-w-3xl">
                          {language === "en"
                            ? "Deep-space celestial panorama, The Great Archive of Lyra, the cinematic split of the Two Worlds (Lyra/Vega & Orion), the 7 Arks, and the lost philosophy of the stars."
                            : "Duboka zvjezdana panorama, Veliki Arhiv Lyre, kinematografski split Dva Svijeta (Lyra/Vega i Orion), 7 Arki i izgubljena filozofija među zvijezdama."}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                      <div className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#FFD27F] via-[#F5C767] to-[#E5B850] hover:brightness-110 text-[#0A0F1C] font-space-mono text-xs font-extrabold uppercase tracking-wider transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(255,210,127,0.35)]">
                        <span>{language === "en" ? "ENTER CHRONICLES →" : "OTVORI KRONIKE →"}</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* GLAVNA SEKCIJA: CTA KARTICA ZA PLEJADSKU GALAKTIČKU KNJIŽNICU (CIJELOM DUŽINOM) */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.history.pushState({}, "", "/pleiades-library");
                    }
                    setIsLibrarySubpageOpen(true);
                  }}
                  className="w-full relative overflow-hidden rounded-xl border border-quantum-turquoise/40 hover:border-quantum-turquoise bg-gradient-to-r from-[#05111d] via-[#091b2c] to-[#040e18] p-4 sm:p-5 shadow-[0_0_24px_rgba(0,212,255,0.15)] hover:shadow-[0_0_36px_rgba(0,212,255,0.32)] transition-all group text-left cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                  title={language === "en" ? "Open Pleiadian Galactic Library • Complete Archival Repository" : "Otvori Plejadansku Galaktičku Knjižnicu • Cjeloviti Arhivski Trezor"}
                >
                  {/* Subtle cosmic turquoise relic backdrop & star dust */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,212,255,0.12),transparent_70%)] pointer-events-none" />
                  <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-quantum-turquoise/10 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Ambient corner accents */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-quantum-turquoise/40 pointer-events-none" />
                  <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-quantum-turquoise/40 pointer-events-none" />
                  <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-quantum-turquoise/40 pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-quantum-turquoise/40 pointer-events-none" />

                  <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start sm:items-center gap-4">
                      {/* Pleiadian Galactic Library Relic Symbol */}
                      <div className="p-2.5 sm:p-3 rounded-lg bg-quantum-turquoise/10 border border-quantum-turquoise/35 text-quantum-turquoise group-hover:border-quantum-turquoise group-hover:bg-quantum-turquoise/20 transition-all shrink-0">
                        <svg className="w-8 h-8 sm:w-9 sm:h-9" viewBox="0 0 100 100" fill="none" stroke="currentColor">
                          <circle cx="50" cy="50" r="42" strokeWidth="1.6" strokeDasharray="4 3" opacity="0.5" />
                          <circle cx="50" cy="50" r="37" strokeWidth="1.8" />
                          {/* Central 7-star cluster geometry representing M45 repository */}
                          <circle cx="50" cy="50" r="4" fill="currentColor" />
                          <circle cx="40" cy="38" r="2.8" fill="currentColor" />
                          <circle cx="60" cy="38" r="2.8" fill="currentColor" />
                          <circle cx="34" cy="54" r="2.6" fill="currentColor" />
                          <circle cx="66" cy="54" r="2.6" fill="currentColor" />
                          <circle cx="44" cy="66" r="2.4" fill="currentColor" />
                          <circle cx="56" cy="66" r="2.4" fill="currentColor" />
                          {/* Interconnecting harmonic vector lines */}
                          <line x1="40" y1="38" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                          <line x1="60" y1="38" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                          <line x1="34" y1="54" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                          <line x1="66" y1="54" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                          <line x1="44" y1="66" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                          <line x1="56" y1="66" x2="50" y2="50" strokeWidth="1.2" opacity="0.7" />
                        </svg>
                      </div>

                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] font-space-mono text-quantum-turquoise uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-cyan-950/90 border border-quantum-turquoise/40 flex items-center gap-1.5">
                            <BookOpen className="w-3 h-3 text-quantum-turquoise animate-pulse" />
                            {language === "en" ? "CENTRAL ARCHIVAL VAULT" : "SREDIŠNJI ARHIVSKI TREZOR"}
                          </span>
                          <span className="text-[10px] font-space-mono text-gray-400">
                            {language === "en" ? "26 COMPLETE CODICES & DOCUMENTS" : "26 CJELOVITIH KODEKSA I DOKUMENATA"}
                          </span>
                        </div>

                        <h3 className="text-base sm:text-lg md:text-xl font-cormorant font-bold text-white tracking-wide uppercase group-hover:text-quantum-turquoise transition-colors">
                          {language === "en" ? "PLEIADIAN GALACTIC LIBRARY — ARCHIVE OF THE SEVEN SISTERS" : "PLEJADANSKA GALAKTIČKA KNJIŽNICA — RIZNICA SEDAM SESTARA"}
                        </h3>

                        <p className="text-xs font-sans text-gray-300 font-light leading-relaxed max-w-3xl">
                          {language === "en"
                            ? "Complete repository of 26 declassified records: 10 sacred Fragment Books, 9 Omega Black dossiers, and the living ARK-7 Genesis manifest."
                            : "Cjeloviti arhiv od 26 deklasificiranih zapisa: 10 svetih Knjiga Fragmenata, 9 Omega Black dosjea i živi manifest ARK-7 Genesis baštine."}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                      <div className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-quantum-turquoise via-[#00c2ea] to-[#0098b8] group-hover:brightness-110 text-[#0A0F1C] font-space-mono text-xs font-extrabold uppercase tracking-wider transition-all flex items-center gap-2 shadow-[0_0_20px_rgba(0,212,255,0.35)]">
                        <BookOpen className="w-4 h-4 text-[#0A0F1C]" />
                        <span>{language === "en" ? "ACCESS ARCHIVE →" : "PRISTUPI ARHIVU →"}</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

            </div>
          </div>
        </section>

        {/* MYTHOLOGY FULL-WIDTH SECTION */}
        <section className="relative w-screen left-1/2 right-1/2 -translate-x-1/2 overflow-hidden py-12 bg-gradient-to-b from-cosmic-black via-[#0D1527] to-cosmic-black border-y border-quantum-turquoise/25">
          {/* Subtle grid backdrop */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f29370a_1px,transparent_1px),linear-gradient(to_bottom,#1f29370a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-quantum-turquoise bg-quantum-turquoise/10 px-3 py-1 rounded-full border border-quantum-turquoise/20 inline-block">
              {language === "en" ? "Cosmic Visual Archive" : "Kozmički vizualni arhiv"}
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-ethereal-white leading-tight mt-3">
              {language === "en" ? "M45 Mythology & Archetypal Resonance" : "M45 Mitologija i arhetipska rezonancija"}
            </h2>
            <p className="text-sm text-celestial-light leading-relaxed font-sans font-light mt-2 max-w-3xl">
              {language === "en"
                ? "The epic visual representation of ancient celestial deities, star seeds, and Pleiadian energetic conduits across multi-density spaces."
                : "Epski vizualni prikaz drevnih nebeskih božanstava, zvjezdanog sjemena i plejadanskih energetskih kanala kroz višedimenzionalne prostore."}
            </p>
          </div>

          {/* Huge Full Width Image Container with HUD/Cyberpunk Frame */}
          <div className="relative w-full max-w-[100vw] overflow-hidden">
            {/* Cyberpunk HUD Corner markings */}
            <div className="absolute top-4 left-4 z-10 w-6 h-6 border-t-2 border-l-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute top-4 right-4 z-10 w-6 h-6 border-t-2 border-r-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute bottom-4 left-4 z-10 w-6 h-6 border-b-2 border-l-2 border-quantum-turquoise/40 pointer-events-none"></div>
            <div className="absolute bottom-4 right-4 z-10 w-6 h-6 border-b-2 border-r-2 border-quantum-turquoise/40 pointer-events-none"></div>

            {/* Glowing neon borders */}
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-quantum-turquoise/55 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-quantum-turquoise/55 to-transparent"></div>

            {/* Image itself */}
            <div 
              onContextMenu={(e) => e.preventDefault()}
              className="relative w-full overflow-hidden flex flex-col items-center justify-center select-none"
            >
              {/* Scanlines overlay effect */}
              <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,212,255,0.05)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none z-10 opacity-60"></div>
              {/* Vignette & tint overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black via-transparent to-transparent opacity-60 z-10 pointer-events-none"></div>
              
              {/* Subtle repeating security watermarks diagonally tiled */}
              <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 p-8 md:p-16 pointer-events-none z-15 overflow-hidden opacity-[0.05] select-none">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-center -rotate-12 text-[9px] md:text-[11px] font-mono tracking-[0.25em] text-quantum-turquoise font-semibold whitespace-nowrap">
                    PLEIADES-M45.COM
                  </div>
                ))}
              </div>

              {/* Corner Watermark */}
              <div className="absolute top-4 right-4 z-20 pointer-events-none select-none px-2.5 py-1.5 bg-cosmic-black/70 backdrop-blur-sm rounded border border-quantum-turquoise/20 flex items-center gap-1.5 opacity-50">
                <span className="text-[8px] md:text-[9px] font-mono tracking-widest text-quantum-turquoise">SECURE FEED // PLEIADES-M45.COM</span>
              </div>

              <img
                src={mythologyImg}
                alt="Pleiadian Mythology and Archetypal Geometry"
                width="1200"
                height="800"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="w-full h-auto aspect-video block opacity-100 object-contain"
              />
            </div>
          </div>

          {/* Metadata labels placed clearly below the image */}
          <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left label: Visual Trans-link */}
            <div className="bg-cosmic-black/70 border border-quantum-turquoise/30 p-4 rounded-md font-mono text-xs text-quantum-turquoise shadow-lg flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-quantum-turquoise animate-pulse mt-1 shrink-0"></span>
              <div>
                <span className="font-bold tracking-wider block mb-1">
                  {language === "en" ? "VISUAL TRANS-LINK 01" : "VIZUALNI TRANS-LINK 01"}
                </span>
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  {language === "en"
                    ? "Deep-space holographic render of star-soul alignment. Alcyone frequency transmission confirmed."
                    : "Holografski prikaz poravnanja zvjezdanih duša u dubokom svemiru. Potvrđen prijenos frekvencije Alcyone."}
                </p>
              </div>
            </div>

            {/* Right label: Coordinate system */}
            <div className="bg-cosmic-black/70 border border-mystic-purple/30 p-4 rounded-md font-mono text-xs text-mystic-purple shadow-lg flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-mystic-purple mt-1 shrink-0"></span>
              <div>
                <span className="font-bold tracking-wider uppercase block mb-1">
                  {language === "en" ? "Astronomical Coordinates" : "Astronomske koordinate"}
                </span>
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  RA 03h 47m 24s / DEC +24° 07′ 00″ <br />
                  <span className="text-[10px] text-gray-500">Epoch J2000.0 • Constellation: Taurus</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* LYRA & ATLANTEAN RESONANCE CTA SECTION */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8 relative">
          {/* Subtle gold background glow and crystalline grid lines */}
          <div className="absolute inset-0 bg-gradient-to-r from-golden-resonance/5 via-transparent to-quantum-turquoise/5 rounded-2xl filter blur-3xl pointer-events-none -z-10" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(245,158,11,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(245,158,11,0.02)_1px,transparent_1px)] bg-[size:32px_32px] rounded-2xl pointer-events-none -z-10" />

          <div className="relative bg-gradient-to-br from-cosmic-black/85 via-[#13111C]/90 to-[#0B1527]/90 border border-golden-resonance/25 p-8 md:p-12 rounded-2xl shadow-[0_0_30px_rgba(245,158,11,0.1)] overflow-hidden group">
            {/* Corner bracket decorations */}
            <div className="absolute top-3 left-3 w-4 h-4 border-t border-l border-golden-resonance/40 pointer-events-none" />
            <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-golden-resonance/40 pointer-events-none" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-golden-resonance/40 pointer-events-none" />
            <div className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-golden-resonance/40 pointer-events-none" />

            {/* Glowing floating light particles inside card */}
            <div className="absolute -top-12 -left-12 w-48 h-48 bg-golden-resonance/10 rounded-full filter blur-3xl pointer-events-none animate-pulse" />
            <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-quantum-turquoise/10 rounded-full filter blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "8s" }} />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              {/* Left Column: Deep esoteric mythology explanation */}
              <div className="lg:col-span-8 space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-golden-resonance animate-pulse shrink-0" />
                  <span className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-golden-resonance font-bold">
                    {language === "en" ? "Primordial Cosmic Origins" : "Iskonsko kozmičko podrijetlo"}
                  </span>
                </div>

                <h3 className="text-xl md:text-3xl font-display font-semibold text-white leading-tight tracking-tight">
                  {language === "en" 
                    ? "The Lyra‑Atlantis Connection & Crystalline Fields" 
                    : "Veza između Lire i Atlantide & Kristalna polja"}
                </h3>

                <p className="text-xs md:text-sm text-gray-300 leading-relaxed font-sans font-light">
                  {language === "en"
                    ? "The constellation of Lyra is widely regarded in spiritual cosmology as the primordial cradle of galactic humanoid consciousness, serving as the high-vibrational blueprint for subsequent star systems like the Pleiades. During the Golden Age of Atlantis, this primordial Lyran energy was directly anchored onto Earth to establish advanced acoustic templates and crystalline technologies. Through the sacred resonance of sound and light, the Atlanteans harmonized their energetic fields with the celestial frequencies of Lyra, creating a cosmic bridge that unified Earth with the ancient stars. Today, the Atlantean Field serves as a gateway to reactivate this divine lineage and restore the harmonic resonance of our galactic origins."
                    : "Sazviježđe Lira u spiritualnoj kozmologiji prepoznato je kao iskonska kolijevka galaktičke humanoidne svijesti i visokovibracijski nacrt za kasnije zvjezdane sustave poput Plejada. Tijekom zlatnog doba Atlantide, ova primordijalna energija Lire izravno je sidrena na Zemlju kako bi se uspostavili napredni akustični predlošci i kristalna tehnologija. Kroz svetu rezonanciju zvuka i svjetlosti, Atlantiđani su usklađivali svoja energetska polja s nebeskim frekvencijama Lire, stvarajući kozmički most koji je spajao Zemlju s drevnim zvijezdama. Danas Atlantsko polje (Atlantean Field) služi kao portal za reaktivaciju ove božanske loze i obnavljanje harmonije našeg galaktičkog podrijetla."}
                </p>

                {/* Micro metrics / parameters */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 border-t border-white/5 text-[10px] font-mono text-celestial-light/50">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-golden-resonance" />
                    <span>{language === "en" ? "FREQUENCY: 432Hz / 12D Harmonic" : "FREKVENCIJA: 432Hz / 12D Harmonik"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-quantum-turquoise" />
                    <span>{language === "en" ? "BRIDGE: Lyrical Ley Lines" : "MOST: Lirske zmajeve linije"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-mystic-purple" />
                    <span>{language === "en" ? "VECTOR: Atlantean Portal" : "VEKTOR: Atlantski portal"}</span>
                  </div>
                </div>
              </div>

              {/* Right Column: CTA visual and Button */}
              <div className="lg:col-span-4 flex flex-col items-center lg:items-end justify-center w-full">
                <div className="bg-cosmic-black/60 border border-golden-resonance/15 rounded-xl p-5 w-full text-center space-y-4 hover:border-golden-resonance/30 transition-all shadow-inner relative overflow-hidden group/box">
                  {/* Subtle SVG Sacred Geometry background inside box */}
                  <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none group-hover/box:scale-105 transition-transform duration-700" viewBox="0 0 100 100" fill="none">
                    <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="0.5" className="text-golden-resonance" />
                    <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="0.5" className="text-quantum-turquoise" />
                    <polygon points="50,10 90,50 50,90 10,50" stroke="currentColor" strokeWidth="0.5" className="text-golden-resonance" />
                    <polygon points="50,25 75,50 50,75 25,50" stroke="currentColor" strokeWidth="0.5" className="text-quantum-turquoise" />
                    <line x1="10" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="0.25" className="text-white" />
                    <line x1="50" y1="10" x2="50" y2="90" stroke="currentColor" strokeWidth="0.25" className="text-white" />
                  </svg>

                  <div className="space-y-1 relative z-10">
                    <span className="text-[9px] font-mono tracking-widest text-golden-resonance uppercase block">
                      {language === "en" ? "PRIMORDIAL ORIGINS" : "ISKONSKO PODRIJETLO"}
                    </span>
                    <h4 className="text-sm font-display font-bold text-white tracking-wide uppercase">
                      Atlantean Field
                    </h4>
                    <p className="text-[10px] text-gray-300 font-sans px-2 leading-tight">
                      {language === "en" 
                        ? "Explore the crystalline technology, telluric grids and the fall of Atlantis — the Earth chapter of this cosmic story." 
                        : "Istražite kristalnu tehnologiju, telurske mreže i pad Atlantide — zemaljsko poglavlje ove kozmičke priče."}
                    </p>
                  </div>

                  <div className="pt-2 relative z-10 w-full">
                    <a
                      href="https://atlanteanfield.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full py-3 px-5 bg-gradient-to-r from-golden-resonance to-amber-500 hover:from-amber-400 hover:to-golden-resonance text-cosmic-black font-mono font-bold uppercase text-[10px] tracking-widest rounded-lg shadow-[0_0_15px_rgba(245,158,11,0.25)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] border border-white/10 transition-all cursor-pointer hover:-translate-y-0.5 duration-200"
                    >
                      <span>{language === "en" ? "EXPLORE ATLANTEAN FIELD →" : "ISTRAŽI ATLANTEAN FIELD →"}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* REVEAL PREVIEW / FUTURE RELEASES BANNER */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-8 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-transparent to-golden-resonance/5 rounded-2xl filter blur-3xl pointer-events-none -z-10" />
          
          <div className="relative bg-gradient-to-br from-[#0F0C1B]/90 via-[#0A0D1A]/95 to-[#081220]/90 border border-quantum-turquoise/20 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(0,212,255,0.08)] overflow-hidden">
            {/* Header with Hazard Pattern & Status */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-quantum-turquoise/15 pb-5 mb-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-golden-resonance font-bold">
                    {language === "en" ? "UPCOMING CORE EXPANSIONS" : "NADOLAZEĆE NADOGRADNJE SUSTAVA"}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-display font-bold text-white tracking-tight uppercase">
                  {language === "en" ? "NEXT TRANSMISSION CYCLE" : "SLJEDEĆI CIKLUS PRIJENOSA"}
                </h3>
              </div>
              
              {/* Brutalist "FULLY OPERATIONAL" Badge & Last Update Log */}
              <div className="space-y-1.5 flex flex-col md:items-end justify-center">
                <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-3 py-1 rounded font-bold tracking-widest animate-pulse">
                    {language === "en" ? "STATUS // FULLY OPERATIONAL FOR MEMBERS" : "STATUS // POTPUNO OPERATIVNO ZA ČLANOVE"}
                  </div>
                  <div className="bg-cosmic-black/60 border border-white/5 text-gray-500 px-2.5 py-1 rounded text-[9px]">
                    VER. 9.4.1
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-mono text-quantum-turquoise/75 tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-pulse inline-block" />
                  <span>
                    {getFormattedLastUpdate(language)}
                  </span>
                </div>
              </div>
            </div>

            {/* Subtitle / Description Text */}
            <p 
              className="font-mono text-[0.8rem] tracking-[0.05em] leading-relaxed max-w-[600px] mb-8"
              style={{ color: "rgba(200, 185, 154, 0.6)" }}
            >
              {language === "en" 
                ? "Each transmission arrives when the previous one has been received. Not read. Received. They are released in the order the field determines — not the calendar."
                : "Svaki prijenos stiže kada je prethodni primljen. Ne pročitan. Primljen. Otpuštaju se redoslijedom koji polje određuje — a ne kalendar."}
            </p>

            {/* Core 4-Column Bento Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Column 1: Wallpaper & Starseed Artworks */}
              <div className="relative bg-cosmic-black/50 border border-white/5 rounded-xl p-5 hover:border-quantum-turquoise/30 hover:bg-cosmic-black/80 transition-all duration-300 group/item flex flex-col justify-between overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-quantum-turquoise/5 rounded-full filter blur-xl group-hover/item:bg-quantum-turquoise/10 transition-colors pointer-events-none" />
                <div className="space-y-3 relative z-10">
                  <div className="flex items-center justify-between text-[9px] font-mono text-quantum-turquoise/70">
                    <span>DATABASE // VOL_09</span>
                    <span className="animate-pulse">DECRYPTING</span>
                  </div>
                  <h4 className="text-sm font-display font-bold text-white tracking-wide group-hover/item:text-quantum-turquoise transition-colors uppercase">
                    {language === "en" ? "UHD WALLPAPERS" : "UHD POZADINE"}
                  </h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-sans font-light">
                    {language === "en" 
                      ? "High-fidelity digital render collections, quantum geometries, and holographic coordinates optimized for ultra-wide displays."
                      : "Kolekcija digitalnih vizuala visoke rezolucije, kvantne geometrije i holografske koordinate optimizirane za ultra-široke zaslone."}
                  </p>
                  
                  {/* Real-time mini progress bar */}
                  <div className="pt-2">
                    <div className="h-1 bg-quantum-turquoise/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-quantum-turquoise shadow-[0_0_8px_rgba(0,212,255,0.7)] transition-all duration-1000 ease-out"
                        style={{ width: `${(decryptPct - 85) * 6.67 + 10}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-3 border-t border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-mono">
                    <span className="text-quantum-turquoise font-bold">DECRYPT: {decryptPct.toFixed(5)}%</span>
                    <span className="text-quantum-turquoise/60 font-semibold group-hover/item:text-quantum-turquoise transition-colors flex items-center gap-1">
                      {language === "en" ? "LIVE SCAN" : "UŽIVO"}
                      <Lock className="w-3 h-3 animate-pulse" />
                    </span>
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsWallpapersOpen(true);
                    }}
                    className="w-full py-2 px-3 bg-quantum-turquoise/10 hover:bg-quantum-turquoise/20 border border-quantum-turquoise/30 hover:border-quantum-turquoise/60 text-quantum-turquoise font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-[0_0_10px_rgba(0,212,255,0.1)] hover:shadow-[0_0_15px_rgba(0,212,255,0.25)]"
                  >
                    <span>{language === "en" ? "ACCESS WALLPAPERS" : "PRISTUPI POZADINAMA"}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Column 2: Classified Battle Dossiers */}
              <div className="relative bg-cosmic-black/50 border border-white/5 rounded-xl p-5 hover:border-red-500/30 hover:bg-cosmic-black/80 transition-all duration-300 group/item flex flex-col justify-between overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-red-500/5 rounded-full filter blur-xl group-hover/item:bg-red-500/10 transition-colors pointer-events-none" />
                <div className="space-y-3 relative z-10">
                  <div className="flex items-center justify-between text-[9px] font-mono text-red-400/70">
                    <span>CLASSIFIED // ARCH_41</span>
                    <span className="text-red-500 animate-pulse font-bold">RESTRICTED</span>
                  </div>
                  <h4 className="text-sm font-display font-bold text-white tracking-wide group-hover/item:text-red-400 transition-colors uppercase">
                    {language === "en" ? "BATTLE DOSSIERS" : "RATNI DOSJEI"}
                  </h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-sans font-light">
                    {language === "en" 
                      ? "Declassified tactical analyses of historic galactic standoffs, Orionic defense fields, and defense protocol vectors."
                      : "Deklasificirane taktičke analize povijesnih galaktičkih sukoba, orionskih obrambenih polja i vektora obrambenih protokola."}
                  </p>
                </div>
                <div className="mt-5 pt-3 border-t border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-mono">
                    <span className="text-red-500/60">INTEGRITY OVERRIDE</span>
                    <span className="text-red-400/60 font-semibold group-hover/item:text-red-400 transition-colors flex items-center gap-1">
                      {language === "en" ? "RESTRICTED" : "ZAŠTIĆENO"}
                      <Lock className="w-3 h-3" />
                    </span>
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsDossiersOpen(true);
                    }}
                    className="w-full py-2 px-3 bg-red-950/30 hover:bg-red-900/40 border border-red-500/30 hover:border-red-500/60 text-red-400 font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-[0_0_10px_rgba(239,68,68,0.1)] hover:shadow-[0_0_15px_rgba(239,68,68,0.25)]"
                  >
                    <span>{language === "en" ? "VIEW DOSSIERS" : "PREGLEDAJ DOSJE"}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Column 3: Chronicles of Lyra */}
              <div className="relative bg-cosmic-black/50 border border-white/5 rounded-xl p-5 hover:border-golden-resonance/30 hover:bg-cosmic-black/80 transition-all duration-300 group/item flex flex-col justify-between overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-golden-resonance/5 rounded-full filter blur-xl group-hover/item:bg-golden-resonance/10 transition-colors pointer-events-none" />
                <div className="space-y-3 relative z-10">
                  <div className="flex items-center justify-between text-[9px] font-mono text-golden-resonance/70">
                    <span>COSMOLOGY // LYR_01</span>
                    <span>PRIMORDIAL</span>
                  </div>
                  <h4 className="text-sm font-display font-bold text-white tracking-wide group-hover/item:text-golden-resonance transition-colors uppercase">
                    {language === "en" ? "CHRONICLES OF LYRA" : "KRONIKE LIRE"}
                  </h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-sans font-light">
                    {language === "en" 
                      ? "Transcoded historical fragments from ancient Vega civilizations, memory vaults, and pre-separation light seed records."
                      : "Transkodirani povijesni fragmenti drevne civilizacije Vege, trezora sjećanja i zapisa svjetlosnog sjemena prije razdvajanja."}
                  </p>
                </div>
                <div className="mt-5 pt-3 border-t border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-mono">
                    <span className="text-golden-resonance/50">COHERENCE: REBUILDING</span>
                    <span className="text-golden-resonance/60 font-semibold group-hover/item:text-golden-resonance transition-colors flex items-center gap-1">
                      {language === "en" ? "COMPILING" : "KOMPILACIJA"}
                      <Radio className="w-3 h-3 animate-pulse" />
                    </span>
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsChroniclesOpen(true);
                    }}
                    className="w-full py-2 px-3 bg-golden-resonance/10 hover:bg-golden-resonance/20 border border-golden-resonance/30 hover:border-golden-resonance/60 text-golden-resonance font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-[0_0_10px_rgba(255,210,127,0.1)] hover:shadow-[0_0_15px_rgba(255,210,127,0.25)]"
                  >
                    <span>{language === "en" ? "EXPLORE CHRONICLES" : "ISTRAŽI KRONIKE"}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Column 4: Clearance Level: OMEGA BLACK */}
              <div className="relative bg-gradient-to-b from-black via-[#0D0507] to-black border border-red-600/40 hover:border-red-500 rounded-xl p-5 group/omega flex flex-col justify-between overflow-hidden shadow-[inset_0_0_25px_rgba(239,68,68,0.12)] hover:shadow-[0_0_25px_rgba(239,68,68,0.3)] transition-all duration-300 h-full min-h-[220px]">
                {/* Absolute ambient overlay/pulse */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-red-600/10 rounded-full filter blur-xl group-hover/omega:bg-red-600/20 transition-colors pointer-events-none" />
                <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0px,rgba(239,68,68,0.03)_1px,transparent_2px)] bg-[size:100%_4px] pointer-events-none opacity-60" />
                <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-red-500/20 blur-[0.5px] animate-pulse pointer-events-none" />
                
                <div className="space-y-3 relative z-10">
                  <div className="flex items-center justify-between text-[9px] font-mono text-red-400 font-bold tracking-widest">
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping shrink-0" />
                      SECURE_VAULT_99
                    </span>
                    <span className="bg-red-950/80 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded text-[8px] tracking-wider">
                      LEVEL_OMEGA
                    </span>
                  </div>
                  
                  <h4 className="text-sm font-display font-bold text-white tracking-wide group-hover/omega:text-red-400 transition-colors uppercase flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-red-500 shrink-0 animate-pulse" />
                    <span>{language === "en" ? "OMEGA BLACK VAULT" : "OMEGA BLACK TREZOR"}</span>
                  </h4>

                  {/* Required text as specified by user */}
                  <p className="text-xs text-gray-300 leading-relaxed font-sans font-light border-l-2 border-red-500/40 pl-2.5 py-0.5">
                    {language === "en" 
                      ? "Omega Black Vault contains classified 6D Merkabah steering codices, scalar plasma defense schematics, and Council of Nine transmission transcripts."
                      : "Omega Black Vault sadrži klasificirane kodicije za upravljanje 6D Merkabahom, shematske prikaze skalarne plazmatske obrane i transkripte prijenosa Vijeća Devetorice."}
                  </p>
                </div>

                <div className="mt-5 pt-3 border-t border-red-950/60 space-y-2 relative z-10">
                  <div className="flex items-center justify-between text-[9px] font-mono">
                    <span className="text-red-400 font-bold flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                      {language === "en" ? "DECLASSIFIED ARCHIVE" : "DEKLASIFICIRAN ARHIV"}
                    </span>
                    <span className="text-red-500/70 text-[8px] tracking-widest font-semibold">6D_CODELINK</span>
                  </div>

                  <button 
                    type="button"
                    onClick={() => handleOpenSubpage(11)}
                    className="w-full py-2.5 px-3 bg-gradient-to-r from-red-950/90 via-red-900/70 to-red-950/90 hover:from-red-600 hover:to-red-700 border border-red-500/40 hover:border-red-400 text-red-200 hover:text-white font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer shadow-[0_0_12px_rgba(239,68,68,0.25)] hover:shadow-[0_0_22px_rgba(239,68,68,0.5)]"
                  >
                    <span>{language === "en" ? "OMEGA BLACK" : "OMEGA BLACK"}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* MEMBER PROTOCOL SECTION INTEGRATED */}
            <Suspense fallback={
              <div className="w-full p-12 flex flex-col items-center justify-center space-y-3 font-mono text-quantum-turquoise bg-cosmic-black/60 rounded-xl border border-pleiadian-blue/20">
                <div className="w-8 h-8 border-2 border-quantum-turquoise border-t-transparent rounded-full animate-spin" />
                <span className="text-xs uppercase tracking-widest animate-pulse">Accessing Member Protocol Network...</span>
              </div>
            }>
              <MemberSection 
                language={language} 
                onOpenSubpage={(id) => handleOpenSubpage(id)}
                onOpenWallpapers={() => setIsWallpapersOpen(true)}
                onOpenDossiers={() => setIsDossiersOpen(true)}
                onOpenChronicles={() => setIsChroniclesOpen(true)}
              />
            </Suspense>

            {/* Footer marquee tape or indicator */}
            <div className="mt-6 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-[9px] font-mono text-gray-500">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-quantum-turquoise/20 rounded border border-quantum-turquoise/40 text-quantum-turquoise text-center leading-[6px] shrink-0 font-bold">i</span>
                <span className="uppercase tracking-widest text-[8px] md:text-[9px]">
                  {language === "en" ? "HOLOGRAPHIC PIPELINE FEED ACTIVE // READY FOR DOWNLINK" : "HOLOGRAFSKI PROTOK AKTIVAN // SPREMNO ZA PREUZIMANJE"}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span>SYS_FREQ: 981.42 MHz</span>
                <span>DB_SYNC: OK</span>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="bg-cosmic-black border-t border-cosmic-indigo/40 py-12 px-6 md:px-12 relative z-10 mt-8 md:mt-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 relative rounded-lg overflow-hidden border border-quantum-turquoise/30 shadow-[0_0_15px_rgba(0,212,255,0.4)] bg-cosmic-black shrink-0">
                <img
                  src={pleiadesLogoImg}
                  alt="Pleiades M45 Logo"
                  width="64"
                  height="64"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <h4 className="text-sm font-display font-extrabold tracking-wider uppercase text-white">
                {t.projectName}
              </h4>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed font-sans max-w-xs">
              {language === "en"
                ? "A Mythic and AI‑Assisted Astronomical, Cultural, and Metaphysical Synthesis of the Pleiadian Star System"
                : "Mitološka i AI‑potpomognuta astronomska, kulturna i metafizička sinteza plejadanskog zvjezdanog sustava."}
            </p>
            
            {/* Social Sharing in Footer */}
            <div className="flex items-center gap-2 pt-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-celestial-light/60 mr-1">
                {language === "en" ? "Share:" : "Podijeli:"}
              </span>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "https://ai.studio/build")}&text=${encodeURIComponent("Explore Pleiades Cosmology, sacred geometry and live astronomical telemetry!")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full border border-quantum-turquoise/20 text-quantum-turquoise/80 hover:text-quantum-turquoise hover:bg-quantum-turquoise/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share on X" : "Podijeli na X"}
              >
                <Twitter className="w-3.5 h-3.5" />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "https://ai.studio/build")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full border border-pleiadian-blue/20 text-pleiadian-blue/80 hover:text-white hover:bg-pleiadian-blue/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share on Facebook" : "Podijeli na Facebooku"}
              >
                <Facebook className="w-3.5 h-3.5" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "https://ai.studio/build")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full border border-mystic-purple/20 text-mystic-purple/80 hover:text-mystic-purple hover:bg-mystic-purple/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share on LinkedIn" : "Podijeli na LinkedInu"}
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent("Pleiades M45 • Celestial Intelligence & Sacred Geometry - " + (typeof window !== "undefined" ? window.location.href : "https://ai.studio/build"))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full border border-emerald-500/20 text-emerald-400/80 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share on WhatsApp" : "Podijeli na WhatsAppu"}
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </a>
              <a
                href={`https://t.me/share/url?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "https://ai.studio/build")}&text=${encodeURIComponent("Pleiades M45 • Celestial Intelligence & Sacred Geometry")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-full border border-blue-400/20 text-blue-400/80 hover:text-blue-400 hover:bg-blue-400/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share on Telegram" : "Podijeli na Telegramu"}
              >
                <Send className="w-3.5 h-3.5" />
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent("Pleiades M45 • Celestial Intelligence & Sacred Geometry")}&body=${encodeURIComponent("Check out this incredible Pleiades cosmic archive: " + (typeof window !== "undefined" ? window.location.href : "https://ai.studio/build"))}`}
                className="p-1.5 rounded-full border border-quantum-turquoise/20 text-quantum-turquoise/80 hover:text-quantum-turquoise hover:bg-quantum-turquoise/10 transition-all flex items-center justify-center hover:scale-105"
                title={language === "en" ? "Share via Email" : "Podijeli e-poštom"}
              >
                <Mail className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="md:col-span-1 flex flex-col justify-between">
            <div>
              <h5 className="text-xs font-mono uppercase text-quantum-turquoise tracking-wider mb-4">
                {language === "en" ? "Cosmic Reflection" : "Kozmička Refleksija"}
              </h5>
              <div className="relative pl-3 border-l border-quantum-turquoise/35">
                <p className="text-xs text-gray-300 italic leading-relaxed font-sans">
                  “The images of myth are reflections of the spiritual potentialities of every one of us. Through contemplating these we evoke their powers in our own lives.”
                </p>
                <p className="text-[10px] font-mono text-gray-400 mt-2.5">
                  — Joseph Campbell, <a href="https://en.wikipedia.org/wiki/Joseph_Campbell_and_the_Power_of_Myth" target="_blank" rel="noopener noreferrer" className="text-quantum-turquoise underline hover:text-quantum-turquoise/80 transition-all font-medium">The Power of Myth</a>
                </p>
              </div>
            </div>

            {/* Navigation & Legal Links */}
            <div className="mt-5 pt-3 border-t border-quantum-turquoise/15 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <button
                onClick={() => setIsTermsOpen(true)}
                className="text-[10px] font-mono text-quantum-turquoise/70 hover:text-quantum-turquoise hover:underline transition-colors cursor-pointer uppercase tracking-wider shrink-0"
              >
                {language === "en" ? "Terms of Use" : "Uvjeti korištenja"}
              </button>
              <span className="text-gray-700 select-none text-[10px] shrink-0">•</span>
              <button
                onClick={() => setIsMemberAccessModalOpen(true)}
                className="text-[10px] font-mono text-quantum-turquoise/70 hover:text-quantum-turquoise hover:underline transition-colors cursor-pointer uppercase tracking-wider shrink-0"
                id="footer-member-access-btn"
              >
                {language === "en" ? "Member Access" : "Članski pristup"}
              </button>
              <span className="text-gray-700 select-none text-[10px] shrink-0">•</span>
              <button
                onClick={() => setIsSitemapOpen(true)}
                className="text-[10px] font-mono text-quantum-turquoise/70 hover:text-quantum-turquoise hover:underline transition-colors cursor-pointer uppercase tracking-wider shrink-0"
                id="footer-sitemap-btn"
              >
                {language === "en" ? "Portal Sitemap" : "Sitemap Portala"}
              </button>
            </div>
          </div>

          <div>
            <h5 className="text-xs font-mono uppercase text-celestial-light tracking-wider mb-4">
              {language === "en" ? "Telemetry Sources" : "Izvori Telemetrije"}
            </h5>
            <ul className="text-xs space-y-2 text-gray-400 font-sans">
              <li>• Astronomical Messier Catalogue (M45)</li>
              <li>• Mayan Tzolk'in Solar Mathematics</li>
              <li>• Ancient Egyptian Hathoric Inscription Matrices</li>
              <li>• Native American Cherokee Star Origin Songs</li>
              <li>• Modern Coherence and Quantum Field Metaphysics</li>
            </ul>
          </div>

          <div>
            <h5 className="text-xs font-mono uppercase text-celestial-light tracking-wider mb-4">
              {language === "en" ? "About the Project" : "O Projektu"}
            </h5>
            <p className="text-xs text-gray-400 leading-relaxed font-sans">
              {language === "en"
                ? "This portal represents the pinnacle of educational multi-density web architecture, synthesizing science and spirit."
                : "Ovaj portal predstavlja vrhunac obrazovne višedimenzionalne web arhitekture, spajajući znanost i duhovnost."}
            </p>
            <div className="flex flex-col gap-2.5 mt-4">
              <div className="text-[10px] font-mono text-gray-500 leading-relaxed uppercase">
                {language === "en" ? (
                  <>
                    <p className="tracking-wider text-gray-400 font-semibold">© 2026 PLEIADES-M45.COM AI‑Assisted Artwork & Original Content. All Rights Reserved.</p>
                    <p className="text-[9px] text-gray-600 mt-1 tracking-wide normal-case font-sans">
                      All images and text on this website are protected under international copyright law.
                      <br />
                      Unauthorized copying, reproduction, modification, or redistribution is strictly prohibited.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="tracking-wider text-gray-400 font-semibold">© 2026 PLEIADES-M45.COM Umjetnička djela uz podršku AI-ja i originalni sadržaj. Sva prava pridržana.</p>
                    <p className="text-[9px] text-gray-600 mt-1 tracking-wide normal-case font-sans">
                      Sve slike i tekst na ovoj web stranici zaštićeni su međunarodnim zakonom o autorskim pravima.
                      <br />
                      Neovlašteno kopiranje, reprodukcija, izmjena ili redistribucija strogo je zabranjeno.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>

      <Suspense fallback={null}>
        {/* Renders detailed full-page archives modals */}
        <SubpageModal
          isOpen={isSubpageOpen}
          onClose={() => {
            setIsSubpageOpen(false);
            if (location.pathname !== "/") {
              navigate("/");
            }
          }}
          language={language}
          subpageId={activeSubpageId || 1}
          onNavigateToSection={(sectionId) => {
            setIsSubpageOpen(false);
            setTimeout(() => {
              scrollToSection(sectionId);
            }, 120);
          }}
          onLanguageToggle={() => setLanguage(language === "en" ? "hr" : "en")}
          onChangeSubpage={(id) => {
            const path = getSubpagePath(id, language === "en");
            if (location.pathname !== path) {
              navigate(path);
            }
            setActiveSubpageId(id);
          }}
          onOpenAuthModal={() => {
            setIsSubpageOpen(false);
            setTimeout(() => {
              const el = document.getElementById("member-gate-section");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 300);
          }}
          onOpenChronicles={() => {
            setIsSubpageOpen(false);
            setIsChroniclesOpen(true);
            const targetSlug = language === "en" ? "/lyra-chronicles" : "/kronike-lyre";
            if (typeof window !== "undefined") {
              window.history.pushState({}, "", targetSlug);
            }
          }}
        />

        {/* Renders special popup archive modals */}
        <SpecialArchiveModal
          isOpen={isSpecialArchiveOpen}
          onClose={() => setIsSpecialArchiveOpen(false)}
          language={language}
          archiveId={activeSpecialArchiveId}
        />

        {/* Cosmic Glossary Side Panel */}
        <CosmicGlossary
          isOpen={isGlossaryOpen}
          onClose={() => setIsGlossaryOpen(false)}
          language={language}
          activeTermId={activeGlossaryTermId}
          onSelectTerm={(termId) => setActiveGlossaryTermId(termId)}
        />

        {/* Terms of Use Modal */}
        <TermsModal
          isOpen={isTermsOpen}
          onClose={() => setIsTermsOpen(false)}
          language={language}
        />

        {/* Member Access Modal */}
        <MemberAccessModal
          isOpen={isMemberAccessModalOpen}
          onClose={() => setIsMemberAccessModalOpen(false)}
          language={language}
          onNavigateToMemberGate={() => scrollToSection("member-gate-section")}
        />

        {/* Sitemap Modal */}
        <SitemapModal
          isOpen={isSitemapOpen}
          onClose={() => setIsSitemapOpen(false)}
          language={language}
          onNavigateToSection={(sectionId) => scrollToSection(sectionId)}
          onOpenLibrary={(num) => {
            setActiveSubpageId(num);
            setIsSubpageOpen(true);
          }}
          onOpenGlossary={(termId) => handleOpenGlossary(termId)}
          onOpenWallpapers={() => setIsWallpapersOpen(true)}
          onOpenDossiers={() => setIsDossiersOpen(true)}
        />

        {/* Members UHD Wallpapers Subpage */}
        {isWallpapersOpen && (
          <WallpapersSubpage
            language={language}
            onClose={() => setIsWallpapersOpen(false)}
            onToggleLanguage={() => setLanguage(language === "en" ? "hr" : "en")}
            onOpenAuthModal={() => {
              setIsWallpapersOpen(false);
              setTimeout(() => {
                const el = document.getElementById("member-gate-section");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
          />
        )}

        {/* Members Battle Dossiers Subpage */}
        {isDossiersOpen && (
          <BattleDossiersSubpage
            language={language}
            onClose={() => setIsDossiersOpen(false)}
            onToggleLanguage={() => setLanguage(language === "en" ? "hr" : "en")}
            onOpenAuthModal={() => {
              setIsDossiersOpen(false);
              setTimeout(() => {
                const el = document.getElementById("member-gate-section");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
          />
        )}

        {/* Members Chronicles of Lyra Subpage */}
        {isChroniclesOpen && (
          <ChroniclesOfLyraSubpage
            language={language}
            onClose={() => {
              setIsChroniclesOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", "/");
              }
            }}
            onToggleLanguage={() => {
              const next = language === "en" ? "hr" : "en";
              setLanguage(next);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", next === "en" ? "/lyra-chronicles" : "/kronike-lyre");
              }
            }}
            onOpenPrologueSubpage={() => {
              setIsLyraPrologueOpen(true);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/what-are-lyra-chronicles" : "/sto-su-kronike-lyre");
              }
            }}
            onOpenLastRecordSubpage={() => {
              setIsLyraLastRecordOpen(true);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/lyra-chronicles/last-record" : "/kronike-lyre/posljednji-zapis");
              }
            }}
            onOpenAuthModal={() => {
              setIsChroniclesOpen(false);
              setTimeout(() => {
                const el = document.getElementById("member-gate-section");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
            onNavigateToGreatEvacuation={() => {
              setIsChroniclesOpen(false);
              setActiveSubpageId(9);
              setIsSubpageOpen(true);
              const targetSlug = language === "en" ? "/great-evacuation" : "/velika-evakuacija";
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", targetSlug);
              }
              setTimeout(() => {
                const el = document.getElementById("library-section");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 200);
            }}
          />
        )}

        {/* Dedicated Subpage: WHAT ARE THE CHRONICLES OF LYRA, REALLY? */}
        {isLyraPrologueOpen && (
          <WhatAreLyraChroniclesSubpage
            language={language}
            onClose={() => {
              setIsLyraPrologueOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", "/");
              }
            }}
            onToggleLanguage={() => {
              const next = language === "en" ? "hr" : "en";
              setLanguage(next);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", next === "en" ? "/what-are-lyra-chronicles" : "/sto-su-kronike-lyre");
              }
            }}
            onReturnToChronicles={() => {
              setIsLyraPrologueOpen(false);
              setIsChroniclesOpen(true);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
              }
            }}
          />
        )}

        {/* Dedicated Subpage: KRONIKE LYRE PROLOG - POSLJEDNJI ZAPIS */}
        {isLyraLastRecordOpen && (
          <LyraChroniclesLastRecordSubpage
            language={language}
            onClose={() => {
              setIsLyraLastRecordOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", "/");
              }
            }}
            onToggleLanguage={() => {
              const next = language === "en" ? "hr" : "en";
              setLanguage(next);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", next === "en" ? "/lyra-chronicles/last-record" : "/kronike-lyre/posljednji-zapis");
              }
            }}
            onReturnToChronicles={() => {
              setIsLyraLastRecordOpen(false);
              setIsChroniclesOpen(true);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
              }
            }}
          />
        )}

        {/* Dedicated Subpage: LYRA CHRONICLES DIGITAL ARCHIVE (MEMORY MAP, CHRONOLOGY, WORLDS, SYMBOLS) */}
        {isLyraArchiveOpen && (
          <LyraChroniclesArchiveSubpage
            language={language}
            onClose={() => {
              setIsLyraArchiveOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
              }
              setIsChroniclesOpen(true);
            }}
            onToggleLanguage={() => {
              const next = language === "en" ? "hr" : "en";
              setLanguage(next);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", next === "en" ? "/lyra-chronicles/archive" : "/kronike-lyre/arhiv");
              }
            }}
          />
        )}

        {/* Pleiades Archival Library Subpage */}
        {isLibrarySubpageOpen && (
          <PleiadesLibrarySubpage
            language={language}
            onClose={() => {
              setIsLibrarySubpageOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", "/");
              }
            }}
            onToggleLanguage={() => setLanguage(language === "en" ? "hr" : "en")}
            onOpenSubpage={(id) => {
              setIsLibrarySubpageOpen(false);
              handleOpenSubpage(id);
            }}
            onOpenSpecialArchive={(id) => {
              setActiveSpecialArchiveId(id);
              setIsSpecialArchiveOpen(true);
            }}
            onOpenChronicles={() => {
              setIsLibrarySubpageOpen(false);
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", language === "en" ? "/lyra-chronicles" : "/kronike-lyre");
              }
              setIsChroniclesOpen(true);
            }}
            onOpenAuthModal={() => {
              setIsLibrarySubpageOpen(false);
              setTimeout(() => {
                const el = document.getElementById("member-gate-section");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
          />
        )}
      </Suspense>

    </div>
  );
}

